/** What /api/v1/me returns. Only what the caller is entitled to know about themselves. */
export interface Me {
  user: string
  roles: string[]
  seesAllData: boolean
  /**
   * Whether to OFFER the View As control. Not whether it will be honoured:
   * the API refuses without it, and the database refuses again independently.
   * Flipping this in a debugger changes what the menu shows and nothing else.
   */
  canViewAsData: boolean
  scopeNodeCount: number
  /**
   * Slugs of the reports this identity may open. The sidebar is built from it.
   *
   * NOT a control — every report procedure re-checks entitlement and answers
   * DENIED_NO_ACCESS however this reads. What it prevents is the menu offering a
   * report the platform will refuse to open, which is how an administrator ends
   * up believing somebody has access when they do not.
   */
  reports: string[]
  /**
   * Which permission model the database runs. Under 'v2' the screens the switch
   * hour retires are not offered (the API answers them 410 anyway). Optional: an
   * API from before Part 16 does not send it, which reads as 'legacy'.
   */
  permissionsModel?: 'legacy' | 'v2'
  /**
   * Under View As: whether the borrowed address is a registered principal.
   * False means the platform has never seen them, which is a different answer
   * from "they hold nothing" and must not render as an empty platform.
   */
  targetIsRegistered: boolean
  /**
   * What is closed right now, and whether this caller passes through it.
   *
   * Read BEFORE the shell is drawn. The client used to discover a window only by
   * being refused, which meant signing somebody in, drawing the whole platform
   * around them, and then printing a small panel where the report should have
   * been — a door-by-door refusal reads as a broken product, not a closed one.
   */
  maintenance: MaintenanceState
  /**
   * WHO IS REALLY AT THE KEYBOARD, or null on an ordinary session.
   *
   * Under Login As every other field on this object belongs to the person being
   * acted as — `user` included, because they ARE the session. This is the only
   * thing that says otherwise, and the screen needs it twice: the banner
   * announces whose permissions are being rendered, and the sidebar footer must
   * go on naming whoever actually signed in.
   *
   * It is also the safeguard for a session that survives a reload. The tab can
   * hold a belief the server has stopped honouring — the target deactivated,
   * the capability revoked — and a banner that keeps claiming an identity the
   * server is not applying is worse than no banner at all. The client compares
   * this against what it thinks and drops its own state when they disagree.
   */
  actor: Actor | null
  /**
   * The reader's remembered choices (036, 4 Oct 2026). Here rather than behind
   * a call of its own because every report waits for /me before it draws, so a
   * remembered currency is known on the first render and the figures are not
   * fetched twice. Under Login As, the target's. Absent from an older API, and
   * null when the read failed -- both mean "nothing remembered".
   */
  settings?: ReaderSettings | null
  /**
   * May they come in (05 6.1)? Under the new model only; null or absent under
   * legacy, where everybody signed in comes in. A refused person reaches nothing
   * but /me, so the app shows "Access denied" before it draws anything (7.9).
   */
  entry?: MeEntry | null
  /** Their Mindzer HR role, while they may come in. Null otherwise, and under legacy. */
  hrRole?: { id: number; label: string | null } | null
  correlationId: string
}

/** /me's answer to "may they come in" (05 6.1). */
export interface MeEntry {
  /** Their entry, phone aside: a system role or an approval in force. */
  allowed: boolean
  /** Whether the server saw a phone, and if so whether phone access is on (6.3). */
  phone: 'not-a-phone' | 'allowed' | 'refused'
  /** The precise DENIED_* code: only to an administrator signed in as them. */
  adminReason: string | null
  /** The server's sentence for adminReason, for that administrator. */
  adminExplanation?: string | null
}

/** True when the server keeps this session out: refused entry, or a phone with phone access off. */
export function entryRefused(me: Pick<Me, 'entry'> | null | undefined): boolean {
  return !!me?.entry && (!me.entry.allowed || me.entry.phone === 'refused')
}

/** What /me says the reader chose last time. */
export interface ReaderSettings {
  /**
   * The display currency: 'local', 'usd', or a code with TODAY's offered rate.
   * A code the platform has stopped offering is never sent; the reader gets the
   * default instead.
   */
  currency: { value: string; ratePerUsd: number | null } | null
}

/** The signed-in person, when they are acting as somebody else. */
export interface Actor {
  email: string
  /** Their Entra object id. Never the target's. */
  objectId: string
}

/**
 * One person in the Login As picker — and, for most of them, one person who
 * CANNOT be picked.
 *
 * The list deliberately carries both populations. Measured on the clone,
 * 17 Sep 2026: 377 people appear in the ERP permission table and 12 have ever
 * signed in. Listing only the selectable ones would remove 365 names, and an
 * administrator searching for somebody they are about to grant access to would
 * find nothing — with no way to tell "has never opened MBI" from "I typed it
 * wrong".
 */
export interface ActAsCandidate {
  /**
   * Their Entra object id, or null when they have never signed in.
   *
   * Null is what makes them unselectable: acting resolves a target by oid, and
   * they have none. It is not a rendering hint — the API refuses regardless.
   */
  oid: string | null
  email: string
  displayName: string | null
  /** Platform roles, comma-separated, or null. Shown because acting takes them on. */
  roles: string | null
  /**
   * Row-scope grants they hold. Zero means acting as them shows an empty
   * platform — which is true, and is what they see when they sign in, but is
   * worth knowing before rather than after.
   */
  grantRows: number
  lastSeenUtc: string | null
  /** False for the never-signed-in half of the list. */
  canActAs: boolean
  /**
   * Under the new model, whether they may come in: 'ALLOWED' or the refusal
   * code (S17). Absent or null under legacy, where grantRows counts permission
   * rows; under the new model grantRows counts the reports their scopes open.
   */
  entryState?: string | null
}

/** Who an acting session is rendering as, returned when one begins. */
export interface ActAsTarget {
  oid: string
  email: string
  displayName: string | null
}

/**
 * One closed thing.
 *
 * Distinct from {@link MaintenanceWindow}, which is the ADMIN console's row and
 * carries what an operator needs to lift it. This is what a user is told: the
 * operator's message, when it lifts, and nothing internal.
 */
export interface MaintenanceNotice {
  /** 'platform' or 'report:&lt;slug&gt;'. */
  scope: string
  /** The report this closes, or null for platform scope. */
  slug: string | null
  /** The operator's own words. Never the window's internal Reason. */
  message: string
  expectedEndUtc: string
  /**
   * True means the API is deliberately still serving reads, so the report stays
   * open behind a banner rather than being replaced. A client that treated every
   * window as closed would contradict a server that is still answering.
   */
  allowReadGrace: boolean
}

/** Maintenance as it applies to the caller who asked. */
export interface MaintenanceState {
  /**
   * Whether this caller is served regardless — a System Admin, so that the
   * person who has to lift a window is never shut out by it. Always false under
   * View As, matching the middleware: availability is part of what the borrowed
   * identity experiences.
   */
  bypass: boolean
  platform: MaintenanceNotice | null
  /** Closed reports, among the ones this identity may open. Empty under platform scope. */
  reports: MaintenanceNotice[]
}

/** Nothing closed. The shape the client falls back to when the field is absent. */
export const NOTHING_CLOSED: MaintenanceState = { bypass: false, platform: null, reports: [] }

/**
 * Which field a matrix level groups by.
 *
 * THE SERVER DECIDES THIS, not the client. A caller without SLS_Mod gets no
 * 'Salesperson' level at all — drilling past a team lands straight on
 * 'Customer' — so the client must render the hierarchy it is handed rather than
 * the one the Power BI report happens to have.
 */
export type MatrixLevel = 'BusinessUnit' | 'BusinessTeam' | 'Salesperson' | 'Customer' | 'Document'

/**
 * One node of the receivables matrix, carrying its own subtotals.
 *
 * The eleven measures are the ones on the Power BI report, in its order. Every
 * one is nullable because the database masks a column before summing it: a
 * measure the caller may not see arrives as null, never as zero.
 */
export interface MatrixNode {
  /** Functional currency, or null when this row spans several — then no symbol is shown. */
  currencyCode: string | null
  /** Null where the source row has no value — rendered blank, as Power BI does. */
  nodeKey: string | null
  groupedBy: MatrixLevel
  hasChildren: boolean
  advancePayment: number | null
  totalReceivables: number | null
  totalOverdue: number | null
  over30DaysOverdue: number | null
  d0To30: number | null
  d31To60: number | null
  d61To90: number | null
  d91To120: number | null
  d121To180: number | null
  d181To365: number | null
  dOver365: number | null
  /**
   * Under the new permission model, the figures on this row that leave out values
   * hidden from this reader, as their names here, comma-separated; null when none
   * does, and always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/**
 * The ancestors of the level being requested. Deliberately NOT a level number:
 * the server derives the level from how much of the path arrived, so there is no
 * way to ask for the document level with no path and receive every document in
 * scope in one response.
 */
export interface MatrixPath {
  bu?: string
  bt?: string
  sp?: string
  customer?: string
}

/**
 * THE STANDING FILTER BAR on Receivables — one shape, four tabs.
 *
 * Deliberately separate from {@link MatrixPath}, whose four fields are spelled
 * almost the same and mean something else entirely: a path field says which
 * node of the hierarchy has been expanded, and the server counts how many
 * arrived to decide which level to return. A filter written into the path would
 * silently skip a level. Everything here rides `f`-prefixed query parameters
 * for the same reason.
 *
 * WHICH TABS HONOUR WHICH is a property of the data, not a preference:
 *
 *   Aging Dashboard, By Transaction   all twelve — they read the aging view
 *   Salesperson DSO                   company, unit, team, salesperson only;
 *                                     that view has nine columns and no
 *                                     customer, document, aging period,
 *                                     payment terms or region
 *   Week Comparison                   those four plus customer — the
 *                                     intersection of four different sources,
 *                                     and exactly the grain the weekly snapshot
 *                                     stores, so a filtered week is still fast
 *
 * The page draws only the controls the active tab can honour. An offered
 * control that changes nothing teaches the reader to distrust the report.
 */
export interface RecvFilters {
  /** Multi-select: company CODES, joined with commas on the wire. */
  company?: string[]
  bu?: string
  bt?: string
  /** Absent entirely for a reader without SLS_Mod — the server sends no menu. */
  sp?: string
  /** A SUBSTRING. 5,373 names measured 24 Sep 2026; a menu of those is no menu. */
  cust?: string
  aging?: string
  trxType?: string
  terms?: string
  region?: string
  /** Also a substring. */
  doc?: string
  /** Past due, which is not the same as outstanding: 1,453 open rows are not yet due. */
  overdueOnly?: boolean
  /** The .pbix's "Days Old > 120", generalised and measured against Days_Overdue. */
  minDaysOverdue?: number
}

/**
 * One grid's stored layout, as the server keeps it.
 *
 * `layout` is OPAQUE on this side of the wire in both directions: the browser
 * writes it and the browser reads it, and no SQL will ever join, filter or
 * aggregate it. `schemaVersion` is what lets a build that no longer understands
 * a payload discard it rather than guess at it.
 */
export interface StoredGridLayout {
  gridId: string
  layout: string
  schemaVersion: number
  changedUtc: string
}

/**
 * One saved view: a whole screen a reader arranged and named.
 *
 * `view` is this client's own JSON in both directions -- the server stores the
 * string and never reads inside it, which is what keeps three genuinely
 * different filter shapes out of the database. `pageSlug` is the tab id;
 * since 24 Sep 2026 the platform has one name for a page on both sides.
 */
export interface StoredReaderView {
  viewId: number
  pageSlug: string
  name: string
  isDefault: boolean
  view: string
  schemaVersion: number
  changedUtc: string
}

/** Nothing set. Exported so every reset uses the same object rather than a fresh literal. */
export const NO_RECV_FILTERS: RecvFilters = {}

/** True when anything is set — decides whether "Clear filters" is offered. */
export function hasRecvFilters(f: RecvFilters): boolean {
  return Boolean(
    f.company?.length || f.bu || f.bt || f.sp || f.cust || f.aging ||
    f.trxType || f.terms || f.region || f.doc || f.overdueOnly || f.minDaysOverdue,
  )
}

/**
 * One entry of one filter menu.
 *
 * `code` is what goes back to the server and `label` is what the reader sees;
 * they differ only for companies, which filter by code and display by name. The
 * rows arrive in the order the menus should be drawn — Aging Period is why that
 * matters, since alphabetically its buckets read 121, 181, 241, 31, 61, 91,
 * Over 365 — so nothing here re-sorts them.
 *
 * A KIND CAN BE ABSENT. `salesperson` is missing entirely for a reader without
 * SLS_Mod, and the page must draw no control rather than an empty one.
 */
export interface RecvFilterRow {
  kind:
    | 'company'
    | 'businessUnit'
    | 'businessTeam'
    | 'salesperson'
    | 'agingPeriod'
    | 'trxType'
    | 'paymentTerms'
    | 'region'
  code: string | null
  label: string | null
}

/** One node of the Salesperson DSO hierarchy. */
export interface DsoNode {
  nodeKey: string | null
  groupedBy: 'Company' | 'BusinessUnit' | 'BusinessTeam' | 'Salesperson'
  hasChildren: boolean
  receivables: number | null
  sales365: number | null
  /**
   * NULL, not zero, when there are no sales to divide by.
   *
   * Zero is the BEST possible days-outstanding, so reporting an undefined ratio
   * as 0 puts the least collectable salesperson at the top of the healthy list.
   * The app this replaces does exactly that; this one renders NULL as no answer.
   */
  dso: number | null
  /**
   * Under the new permission model, the figures on this row that leave out values
   * hidden from this reader, as their names here, comma-separated; null when none
   * does, and always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/**
 * Display currency.
 *
 * 'local' is as booked — a mixture of functional currencies across companies,
 * so a total in local is a sum of different units and must never be stamped
 * with one symbol. 'usd' reads the source-computed USD twin of each measure.
 * 'custom' restates those dollars in one currency the reader picked.
 *
 * ---------------------------------------------------------------------------
 * AN OBJECT, NOT A STRING UNION, AND DELIBERATELY SO
 *
 * The obvious third mode is `'local' | 'usd' | 'AED'`. It is the wrong shape,
 * for a reason worth keeping written down: every one of the forty-odd
 * `currency === 'usd'` comparisons in this app would go on COMPILING and
 * silently returning false. Those comparisons write the captions — "local
 * currency, as booked", "adds up to several local currencies" — so a reader in
 * dirhams would be told their figures were as-booked, by a sentence that type-
 * checked perfectly.
 *
 * An object has no overlap with a string literal, so every one of those sites
 * becomes a compiler error and somebody has to decide what it meant: did it
 * mean "is it dollars" or "is it converted at all"? Those are different
 * questions and the old code could not tell them apart.
 */
export type Currency =
  | { mode: 'local' }
  | { mode: 'usd' }
  | { mode: 'custom'; code: string; rate: number }

/** As booked. */
export const LOCAL: Currency = { mode: 'local' }
/** Converted to dollars at source. */
export const USD: Currency = { mode: 'usd' }
/** Dollars restated in `code`, at `rate` local units per dollar. */
export const inCurrency = (code: string, rate: number): Currency => ({ mode: 'custom', code, rate })

/**
 * Is this a single, known unit — as opposed to a mixture of whatever each
 * company books in? True for dollars and for a chosen currency.
 *
 * This is the question nearly every caption is really asking, and the one the
 * old `=== 'usd'` could not express.
 */
export const isConverted = (c: Currency | undefined): boolean => c?.mode !== undefined && c.mode !== 'local'

/** Dollars specifically — the only mode that renders a `$`. */
export const isUsd = (c: Currency | undefined): boolean => c?.mode === 'usd'

/**
 * The unit to print, given what the SERVER said it served.
 *
 * The payload's code wins for a converted figure, because the procedure is what
 * actually decided: ask for a currency it will not vouch for and it serves
 * local and says so. Trusting the client's own request instead is how a screen
 * ends up labelled with a currency nobody converted to.
 */
export function unitOf(c: Currency | undefined, served?: string | null): string | null {
  if (served) return served
  return c?.mode === 'custom' ? c.code : c?.mode === 'usd' ? 'USD' : null
}

/**
 * What goes on the wire. Local is expressed by OMITTING the parameter, which is
 * why this returns undefined rather than the word.
 */
export function toParam(c: Currency | undefined): string | undefined {
  // Undefined means the caller did not ask, which is the same request as local:
  // say nothing, and let the procedure's own default answer.
  if (!c || c.mode === 'local') return undefined
  return c.mode === 'usd' ? 'usd' : c.code
}

/** The four things Week Comparison and Trends can measure. */
export type WeekMetric = 'collections' | 'sales' | 'receivables' | 'overdue'

/** The four ways to slice them. Not all four exist on every metric. */
export type WeekDim = 'Salesperson' | 'BusinessTeam' | 'BusinessUnit' | 'Company'

export interface WeekMember {
  member: string | null
  /**
   * Null when the metric cannot be expressed in the chosen currency —
   * collections has no USD twin column, and a local figure wearing a dollar
   * label is exactly the error the currency work exists to prevent.
   */
  amount: number | null
  /**
   * Under the new permission model, the figures on this row that leave out values
   * hidden from this reader, as their names here, comma-separated; null when none
   * does, and always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

export interface WeekSide {
  start: string
  endExclusive: string
  members: WeekMember[]
}

/** One metric's per-customer figures for both weeks of a salesperson review. */
export interface ReviewMetric {
  metric: WeekMetric
  weekA: WeekMember[]
  weekB: WeekMember[]
}

export interface SalespersonReview {
  salesperson: string
  weekA: { start: string; endExclusive: string }
  weekB: { start: string; endExclusive: string }
  metrics: ReviewMetric[]
}

/** N consecutive weeks of one metric, oldest first. */
export interface WeekTrend {
  weeks: WeekSide[]
}

export interface WeekComparison {
  weekA: WeekSide
  weekB: WeekSide
}

/** Which field a decomposition level breaks down by. */
export type DecomposeLevel = MatrixLevel

/** One bar of the tree. */
export interface DecomposeNode {
  /** Functional currency, or null when this row spans several — then no symbol is shown. */
  currencyCode: string | null
  nodeKey: string | null
  /** Amount Remaining — the measure the Fabric trees analyse. Null when masked. */
  amount: number | null
  totalReceivables: number | null
  totalOverdue: number | null
  /** 4M across one document is a different problem from 4M across two hundred. */
  docCount: number
  /**
   * Under the new permission model, the figures on this row that leave out values
   * hidden from this reader, as their names here, comma-separated; null when none
   * does, and always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/**
 * A level of the tree, plus what the reader may ask for next.
 *
 * `availableFields` is computed in the DATABASE, because only it knows which
 * columns this caller may see. A reader without SLS_Mod never sees a
 * "Salesperson" button at all.
 */
export interface DecomposeResponse {
  groupedBy: DecomposeLevel
  availableFields: DecomposeLevel[]
  nodes: DecomposeNode[]
}

/** Filters already applied, in any order — unlike the matrix this is a set. */
export interface DecomposePath {
  bu?: string
  bt?: string
  sp?: string
  customer?: string
  document?: string
}

/**
 * One open document on the Aging By Transaction page.
 *
 * Nullable throughout, for two different reasons that must not be conflated: a
 * measure is null when the caller may not SEE it (masked before it left the
 * database), and a date or term is null when the source row genuinely has none.
 * The grid renders the first as withheld and the second as blank.
 */
export interface TransactionRow {
  /** Functional currency, or null when this row spans several — then no symbol is shown. */
  currencyCode: string | null
  businessUnit: string | null
  businessTeam: string | null
  salesperson: string | null
  customer: string | null
  paymentTerms: string | null
  docNumber: string | null
  documentDate: string | null
  daysOld: number | null
  dueDate: string | null
  daysOverdue: number | null
  advancePayment: number | null
  totalReceivables: number | null
  totalOverdue: number | null
  over30DaysOverdue: number | null
  d0To30: number | null
  d31To60: number | null
  d61To90: number | null
  d91To120: number | null
  d121To180: number | null
  d181To365: number | null
  dOver365: number | null
}

/** A page of documents, with the size of the whole filtered set. */
export interface TransactionPage {
  /** Rows in the WHOLE filtered set, not this page — what "next page exists" is decided by. */
  total: number
  page: number
  pageSize: number
  rows: TransactionRow[]
}

export interface PermissionUser {
  email: string
  displayName: string
  userClass: string
  grantRows: number
  companies: number
  companyList: string
  /**
   * Holds every (unit, team) that exists in at least one of their companies —
   * i.e. sees a whole company, reached by ENUMERATION. This source has no
   * wildcard, so breadth is a count rather than a flag in the data.
   */
  wholeCompany: boolean
  /**
   * How many of this person's grant rows duplicate another exactly.
   *
   * REPLACED anyWildcard. That asked whether the person held an 'ALL' in a scope
   * column — a real question against the old D365_User_Permission and a
   * meaningless one against D365_UserPermDetails, which has no wildcards.
   * Rendering a constant "No" would have implied a check that never ran.
   */
  duplicateRows: number
}

export interface PermissionGrant {
  company: string
  companyName: string
  businessUnit: string
  businessTeam: string
  breadth: string
  userClass: string
}

export interface PermissionAnomaly {
  severity: "broken" | "review" | "tidy"
  finding: string
  subject: string
  detail: string
  action: string
}

/**
 * One person on a report's access list.
 *
 * Report access and platform roles are the ONLY two things this platform
 * grants. Row scope and column access are D365's and stay read-only.
 */
/**
 * One report in the registry, for the administration picker.
 *
 * NOT the same list as `Me.reports`. That says which reports the caller may
 * OPEN; this says which they may ADMINISTER. A Permission Manager decides who
 * opens a report while holding no report access of their own, so filtering this
 * by what they can open would leave that role with nothing to administer.
 */
export interface AdminReport {
  slug: string
  displayName: string
  dataDomain: string | null
  /** 'data-scoped' or 'role-scoped'. A role-scoped report has no column mask. */
  scopingModel: string | null
  requiredPermission: string | null
  peopleWithAccess: number
  governedColumns: number
}

export interface ReportAccessEntry {
  email: string
  displayName: string | null
  grantedBy: string | null
  grantedUtc: string | null
  reason: string | null
  /**
   * False means they can open the report and see nothing: access and data
   * scope are separate gates and only the first is ours. Rendered, not hidden
   * — "I was granted it and it's empty" is otherwise an unanswerable question.
   */
  knownToD365: boolean
}

export interface GrantAccessRequest {
  /** Comma or newline separated. An admin pastes a list. */
  emails: string
  /** Required. The platform hosts several reports and the API refuses to guess. */
  reportSlug: string
  reason?: string
}

export interface GrantAccessResult {
  requested: number
  added: number
  /** Idempotent no-ops. Reported so an admin who typed the wrong address notices. */
  alreadyHad: number
  unknownToPermissionData: number
  unknownEmails: string[]
}

/**
 * The platform roles. PermissionManager was removed 17 Sep 2026 -- nobody
 * held it, and "we only have admin and master viewer". The new model (048) adds
 * SystemMasterAdmin and SystemDevelopment; MasterViewer is shown as "System
 * Master Reader".
 */
export type PlatformRoleCode = 'SystemAdmin' | 'MasterViewer' | 'SystemMasterAdmin' | 'SystemDevelopment'

/**
 * Who may use developer mode (05 4.12, D5): the System Admin, the System Master
 * Admin (who reaches the web as 'SystemAdmin', CallerAccessor) and System
 * Development. The same rule as the API's DeveloperModeEndpoints.MayUseDeveloperMode
 * and 017's Can_Developer_Mode; the web used to check 'SystemAdmin' alone, so a
 * System Development holder never saw the switch or the explanations (review R-06).
 */
export const mayUseDeveloperMode = (roles: readonly string[]): boolean =>
  roles.includes('SystemAdmin') || roles.includes('SystemDevelopment')

export interface RoleHolder {
  email: string
  displayName: string | null
  roleCode: PlatformRoleCode
  roleName: string
  /** This person reads every company, regardless of the permission table. */
  seesAllData: boolean
  grantedBy: string | null
  grantedUtc: string | null
  reason: string | null
}

/**
 * One report column and the permission(s) that unlock it.
 *
 * The FOURTH gate. D365 says which modules a person holds; this platform
 * decides which of its columns each module unlocks — the ERP has no opinion
 * about a column called "121 To 180 Days".
 */
export interface ColumnGovernanceRow {
  reportColumnId: number
  /** The physical column, exactly as the view spells it. */
  columnName: string
  displayName: string
  /**
   * A grouping dimension: always visible, and the database refuses to restrict
   * it. Hiding one would not restrict data, it would break every page.
   */
  isRequired: boolean
  sortOrder: number
  /** Comma-joined codes, or null when nothing gates it. */
  permissions: string | null
  /** 'always visible' | 'mapped' | 'UNMAPPED - hidden from everyone' (under v2, 'UNMAPPED - system roles only'). */
  status: string
}

export interface PermissionCatalogRow {
  permissionCode: string
  displayName: string
  /** 'module' or 'cost' — the ERP's own two families. */
  kind: string
  notes: string | null
  /**
   * Live holder count. Zero matters: gating a column on a permission nobody
   * holds hides it from everyone, and the screen says so before the press.
   */
  peopleHolding: number
}

export interface ColumnGovernance {
  columns: ColumnGovernanceRow[]
  catalogue: PermissionCatalogRow[]
}

export interface SetColumnPermissionsRequest {
  reportColumnId: number
  /** The complete new set, not a delta. Empty means nobody sees the column. */
  permissions: string[]
  /** Required. Nothing else explains a column vanishing for a whole group. */
  reason: string
}

/** What a re-gating did, measured in PEOPLE rather than codes. */
export interface ColumnPermissionChange {
  columnName: string
  before: string
  after: string
  peopleBefore: number
  peopleAfter: number
  change: number
}

/*
 * COLUMN DEFINITION UNDER THE NEW MODEL (060; D53, owner 28 Oct 2026). A column is
 * Everyone, hidden (the default: no row, the system roles still see it) or gated by
 * one or more of its report's column groups; a column the report needs is always
 * shown. Admins make the groups, one report each. Names are the procedures' own,
 * camel-cased by the API.
 */

/** Who sees a column. REQUIRED: a column the report needs, always shown, never set. */
export type ColumnVisibility = 'REQUIRED' | 'EVERYONE' | 'HIDDEN' | 'GROUPS'

/** One column of a report on Column definition. */
export interface ColumnDefinitionRow {
  reportColumnId: number
  columnName: string
  displayName: string
  isRequired: boolean
  sortOrder: number
  visibility: ColumnVisibility
  /** Its groups' IDs when GROUPS, comma separated ("12, 15"); else null. */
  groupIds: string | null
  /** Its groups' names, in the same order; else null. */
  groupNames: string | null
  /** People on the report who see it, the system roles aside. */
  peopleSeeing: number
  /** The setting's version a change quotes; null when hidden or required. */
  version: string | null
}

/** One column group of a report, with what uses it: deleted only when all four are 0 (51629). */
export interface ColumnGroupRow {
  groupId: number
  groupName: string
  description: string | null
  columnsN: number
  /** The columns it gates, by their screen names, in the report's order. */
  columnNames: string | null
  rolesN: number
  personSettingsN: number
  extraScopesN: number
  /** People whose access on the report carries it. */
  peopleHolding: number
  version: string
  changedByEmail: string | null
}

/** GET /api/v1/admin/column-definition/{report}. */
export interface ColumnDefinition {
  columns: ColumnDefinitionRow[]
  groups: ColumnGroupRow[]
  columnCount: number | null
  hiddenCount: number | null
  groupCount: number | null
  outcome: string | null
}

/** Who sees one column: the whole setting (D53). */
export interface ColumnSettingBody {
  visibility: 'HIDDEN' | 'EVERYONE' | 'GROUPS'
  /** This report's groups, for GROUPS; [] otherwise. */
  groupIds: number[]
  /** The setting's version as read; null when the column was hidden. */
  expectedVersion: string | null
  reason?: string
}

/** What a column change does (sec.usp_Admin_Set_Column_Rules_MBI's summary; its own keys). */
export interface ColumnSettingEffect {
  outcome: string
  summary: {
    Outcome: string
    Column_Name: string
    Visibility_Before: 'HIDDEN' | 'EVERYONE' | 'GROUPS'
    Visibility_After: 'HIDDEN' | 'EVERYONE' | 'GROUPS'
    Groups_Before: string | null
    Groups_After: string | null
    People_Before: number
    People_After: number
    Version: string | null
  } | null
}

/** One column group and everything that uses it (GET /api/v1/admin/column-groups/{id}). */
export interface ColumnGroupPage {
  group: {
    groupId: number
    reportSlug: string
    reportName: string
    groupName: string
    description: string | null
    version: string
    changedByEmail: string | null
    changeReason: string | null
  } | null
  columns: { reportColumnId: number; columnName: string; displayName: string }[]
  roles: { roleId: number; roleName: string | null; roleFunction: string | null }[]
  people: { mindzerUserId: number; displayName: string | null; effect: 'A' | 'R' }[]
  scopes: { extraScopeId: number; mindzerUserId: number; displayName: string | null; compId: string; businessUnit: string | null; businessTeam: string | null }[]
}

/** A column group made, or renamed or described (its report never changes). */
export interface ColumnGroupBody {
  reportSlug?: string
  name: string
  description: string | null
  /** The group's version as read, to change one. */
  expectedVersion?: string | null
  reason: string
}

/** What a column group save or delete did. */
export interface ColumnGroupEffect {
  outcome: string
  savedId?: number | null
  summary: { Outcome: string; Group_Id: number; Report_Slug: string; Version?: string | null; Group_Name?: string } | null
}

export interface SetRoleRequest {
  email: string
  roleCode: PlatformRoleCode
  /** true grants, false revokes. The database's guards apply to both. */
  grant: boolean
  /** Required by the database when the role sees all data. */
  reason?: string
}

export interface MaintenanceWindow {
  scope: string
  message: string
  expectedEndUtc: string
  allowReadGrace: boolean
}

export interface MaintenanceStatus {
  active: boolean
  /**
   * EVERY open window. The console must render all of them, because each one is
   * separately liftable and a window it cannot show is a window nobody can
   * cancel from the UI.
   *
   * Before 5 Aug 2026 there was only `window`, filled from a read that returned
   * platform-scoped windows only — so opening a window on a report appeared to
   * do nothing at all.
   */
  windows: MaintenanceWindow[]
  /** The platform window if there is one, else the first. Kept for compatibility. */
  window: MaintenanceWindow | null
}

export interface SetMaintenanceRequest {
  scope: string
  reason: string
  message?: string
  durationMinutes: number
  allowReadGrace: boolean
}

/**
 * The API's failure modes, as distinct types rather than one generic error.
 *
 * This distinction is the whole point of the backend design and it has to
 * survive into the UI. "No permissions" and "no matching rows" look identical
 * if both render as an empty table — which is precisely the confusion the
 * legacy app created, where a misconfigured user and a user with a genuinely
 * empty book saw the same blank screen and neither could tell which they were.
 */
export type ApiFailure =
  /** 403. The caller is authenticated but entitled to no data. Never an empty 200. */
  | { kind: 'no-permissions'; correlationId: string }
  /**
   * 403. Authenticated, but refused — and the REASON varies: not on a report's
   * access list, no module permission, no scope granted, or no platform role.
   * `explanation` is the API's sentence for this particular refusal; the screen
   * must render it rather than assuming which one happened.
   */
  | {
      kind: 'forbidden'
      title: string
      explanation?: string
      correlationId: string
      /**
       * The procedure's DENIED_* outcome, verbatim — BRANCH ON THIS, never on
       * `title`. The API has sent it all along; the client dropped it until
       * 22 Sep 2026, so the one screen that needed to tell refusals apart
       * (Salesperson Review) matched on the title's English instead. Naming a
       * refusal more helpfully on the server then silently broke that screen.
       * Titles are prose for a reader and are allowed to change; this is not.
       */
      reason?: string
      /**
       * Under the new permission model, the precise DENIED_* code, present only
       * when an administrator is signed in as the person (05 6.4): a reader is
       * refused in one sentence and is not told which gate stopped them.
       */
      adminReason?: string
    }
  /**
   * 404. Used by /dev/measure for "nothing documented", "developer mode is off"
   * and "you are not a System Admin" alike, so that the three are
   * indistinguishable from outside. A distinct kind because "there is nothing
   * to show you" and "something went wrong" are different screens.
   */
  | { kind: 'not-found'; correlationId?: string }
  /** 503. A maintenance window is open. */
  | { kind: 'maintenance'; message: string; scope: string; expectedEndUtc: string; retryAfterSeconds: number }
  /**
   * 401. Not signed in, or the token was rejected. `refused` when the API was
   * sent a token and turned it down — an account from a company MBI does not
   * accept — rather than no token being at hand.
   */
  | { kind: 'unauthenticated'; refused?: boolean }
  /**
   * Sign-in could not even be attempted, because the Entra app registration is
   * not set up to allow it. Distinct from 'error' because the reader is not a
   * user who should raise a ticket — it is whoever administers the registration,
   * and the message tells them which setting to change.
   */
  | { kind: 'config'; message: string }
  /**
   * Anything else, including the network being down.
   *
   * `outcome` carries the machine-readable DENIED_* code where the API sent one
   * beside the prose, exactly as `reason` does on a 403. Added 24 Sep 2026
   * because without it a screen could not tell a refusal it had DESIGNED from a
   * 500 it had not: renaming a view onto a name already in use is answered with
   * a sentence worth showing the reader, and a missing stored procedure is
   * answered with "An error occurred while processing your request." Both
   * arrived here as { kind: 'error', title }, and the rename row printed the
   * second one as though it were advice.
   */
  | {
      kind: 'error'
      title: string
      correlationId?: string
      outcome?: string
      /**
       * The access map's machine-readable code on a 409 (Part 17):
       * CHANGED_SINCE_READ, WARNINGS_CHANGED or ACCESS_UPDATE_RUNNING. Branch on
       * this, never on the title.
       */
      reason?: string
    }

export class ApiError extends Error {
  readonly failure: ApiFailure

  constructor(failure: ApiFailure) {
    super(describe(failure))
    this.name = 'ApiError'
    this.failure = failure
  }
}

function describe(f: ApiFailure): string {
  switch (f.kind) {
    case 'no-permissions':
      return 'You have no data permissions on this platform.'
    case 'forbidden':
      return f.title
    case 'maintenance':
      return f.message
    case 'unauthenticated':
      return 'Not signed in.'
    case 'not-found':
      return 'There is nothing documented for this.'
    case 'config':
      return f.message
    case 'error':
      return f.title
  }
}

/* ===========================================================================
   SALES — the merged Power BI sales report (see artifacts/sales-import).
   Shapes mirror SalesReportEndpoints.cs exactly; a masked figure is null,
   never zero, because the procedures mask before they aggregate.
   =========================================================================== */

export type SalesPeriod = 'ytd' | 'mtd' | 'custom'

/**
 * The custom range's bounds, [from, to) exclusive on the right, as yyyy-MM-dd.
 * Sent only when period is 'custom'; the procedures fall back to 'ytd' on a
 * missing or malformed pair rather than erroring.
 */
export interface SalesRange {
  from?: string
  to?: string
}

/**
 * The grouping dimensions. 'Company' is EXPLORER-ONLY: the tree's path steps
 * become standing filter parameters, and its @Company filter takes the code
 * while a grouping yields the name — so the tree keeps the six below.
 */
export type SalesDimension =
  | 'Company'
  | 'BusinessUnit'
  | 'BusinessTeam'
  | 'Salesperson'
  | 'Customer'
  | 'Product'
  | 'Supplier'

/**
 * What the EXPLORER may group by: every dimension above, plus the two
 * countries (29 Sep 2026) -- the selling company's and the customer's.
 *
 * A separate type, not two more members of SalesDimension, so the TYPE says
 * what Company's exclusion from the tree only says in a comment: the tree
 * cannot be handed a country. Its path steps become standing filters and
 * there is no country filter, so a country step would draw unfiltered bars
 * under a filtered label. The spellings must equal what 021's allowlist
 * accepts, character for character -- an unknown value is silently read as
 * BusinessUnit there.
 */
export type SalesExplorerDimension = SalesDimension | 'Country' | 'CustomerCountry'

/**
 * What a drill LEVEL below the first may be (30 Sep 2026): any Explorer
 * grouping, plus 'Item' -- the item number, which is a level but never a
 * grouping of its own. Same spellings as 021's @Drill2/@Drill3 vocabulary.
 */
export type SalesDrillLevel = SalesExplorerDimension | 'Item'

/** The levels the reader chooses: 2, 3 and 4, in order. */
export type SalesDrillChoice = [SalesDrillLevel, SalesDrillLevel, SalesDrillLevel]

/** Each grouping's default levels 2 to 4 -- the same table 021's header holds. */
export const SALES_DEFAULT_DRILL: Record<SalesExplorerDimension, SalesDrillChoice> = {
  Country: ['Company', 'BusinessUnit', 'BusinessTeam'],
  Company: ['BusinessUnit', 'BusinessTeam', 'Salesperson'],
  BusinessUnit: ['Customer', 'Product', 'Salesperson'],
  BusinessTeam: ['Salesperson', 'Customer', 'Product'],
  Salesperson: ['Customer', 'Product', 'Item'],
  CustomerCountry: ['Customer', 'Product', 'Salesperson'],
  Customer: ['Product', 'Salesperson', 'Item'],
  Product: ['Customer', 'Salesperson', 'Item'],
  Supplier: ['Product', 'Item', 'Customer'],
}

/**
 * The chain a request resolves to -- 021's fallback rule, restated for the
 * demo client and for the page's own labels before an answer arrives. Level 2
 * is the reader's choice unless it repeats level 1; each deeper level is
 * theirs unless it repeats a level above it, and otherwise the first default
 * not already used. The procedure is the authority; the response's drillChain
 * wins.
 */
export function resolveSalesDrill(
  dim: SalesExplorerDimension,
  drill2?: string | null,
  drill3?: string | null,
  drill4?: string | null,
): [SalesDrillLevel, SalesDrillLevel, SalesDrillLevel, SalesDrillLevel] {
  const known = (v?: string | null): SalesDrillLevel | null =>
    v && (v === 'Item' || v in SALES_DEFAULT_DRILL) ? (v as SalesDrillLevel) : null
  const [d2, d3, d4] = SALES_DEFAULT_DRILL[dim]
  const x2 = known(drill2), x3 = known(drill3), x4 = known(drill4)
  const c2: SalesDrillLevel = x2 && x2 !== dim ? x2 : d2
  const used2 = (v: SalesDrillLevel) => v === dim || v === c2
  const c3: SalesDrillLevel =
    x3 && !used2(x3) ? x3
      : !used2(d3) ? d3
        : !used2(d2) ? d2
          : (['Customer', 'Product', 'Salesperson'] as SalesDrillLevel[]).find((v) => !used2(v))!
  const used3 = (v: SalesDrillLevel) => used2(v) || v === c3
  const c4: SalesDrillLevel =
    x4 && !used3(x4) ? x4
      : !used3(d4) ? d4
        : !used3(d3) ? d3
          : !used3(d2) ? d2
            : (['Customer', 'Product', 'Salesperson', 'Item'] as SalesDrillLevel[]).find((v) => !used3(v))!
  return [dim, c2, c3, c4]
}

/** The standing filter bar, applied to every sales question alike. */
export interface SalesFilters {
  /**
   * Company is MULTI-SELECT: a list of DATAAREAID codes, sent comma-joined.
   * Empty or absent means every company the caller may see — there is no
   * "none" state, so the report can never blank itself.
   */
  company?: string[]
  bu?: string
  bt?: string
  sp?: string
  /**
   * The three added 10 Sep 2026. They are abbreviated like bu/bt/sp for a
   * reason beyond brevity: /tree already spends `customer` and `supplier` on
   * the drill PATH, and /lines spent `region` and `supplier` on its own
   * dropdowns, so the standing filters needed names of their own or one would
   * have quietly overwritten the other.
   */
  /**
   * Supplier_Name — a LIST, matched exactly, chosen from the option list.
   *
   * Multi-select since 25 Sep 2026. It travels joined by U+001F rather than by
   * a comma, because these are ERP names: "Acme, Inc." split on a comma becomes
   * two members that match nothing and the screen goes quietly empty. The tree
   * path has always sent its members this way; this is the same contract.
   */
  sup?: string[]
  /** Cust_Region, matched exactly — ISO-2 codes, 76 of them in the data. */
  reg?: string
  /**
   * Customer_Name, matched as a SUBSTRING rather than chosen from a list.
   * 9,742 distinct customer names (10 Sep 2026) is not a dropdown anyone can
   * scroll, and not a payload worth downloading to open a page.
   */
  cust?: string
}

/**
 * One currency a reader may restate figures into.
 *
 * `ratePerUsd` is LOCAL UNITS PER US DOLLAR — the same orientation the fact
 * tables use, so a figure in this currency is its dollar value MULTIPLIED by
 * the rate. The multiply itself happens in the procedure, never here: this
 * carries the rate so the screen can say what it converted at, not so the
 * browser can do the converting.
 *
 * `note` is present only where somebody had to decide the rate, and is worth
 * showing beside the currency — a reader choosing it should see that it was a
 * judgement rather than an agreed fact. Since 045 (6 Oct 2026) every offered
 * rate is D365's own, so nobody decides one and this is always null: the only
 * notes 045 writes say why a currency is withheld, and a withheld currency is
 * never sent. Kept on the wire so a decided rate can return without a change
 * here.
 */
export interface CurrencyRate {
  code: string
  ratePerUsd: number
  note: string | null
}

export interface SalesOverview {
  sales: number | null
  salesPY: number | null
  gp: number | null
  gpPY: number | null
  gpPct: number | null
  gpPctPY: number | null
  qty: number | null
  qtyPY: number | null
  customers: number | null
  salespeople: number | null
  items: number | null
  invoices: number | null
  /** Sales this week — the legacy WK_SLS figure, restored by the coverage audit. */
  salesWK: number | null
  /* NO currency or unit here, and that is a measured decision rather than an
     omission: deriving them cost this call +1,799ms on the bypass path (640ms
     to 2,439ms, clone, 16 Sep 2026), and it is the call the screen waits for on
     every filter change. The TABLES carry the code per row instead, where a
     group is usually one company and the answer is actually useful — see
     SalesExplorerRow.currencyCode. */
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

export interface SalesExplorerRow {
  member: string | null
  hasChildren: boolean
  totalRows: number
  sales: number | null
  salesPY: number | null
  gp: number | null
  gpPY: number | null
  gpDiff: number | null
  gpPct: number | null
  gpPctPY: number | null
  qty: number | null
  qtyPY: number | null
  /** What THIS ROW is in — null when the group spans several currencies. A
      group is usually one company, so this is usually a real code even where
      the whole selection has many and the KPI head could say nothing. */
  currencyCode: string | null
  /** The unit the qty columns are in; null when the group spans several. */
  unit: string | null
  /**
   * Whether this row adds quantities in SEVERAL units together (4 Oct 2026).
   * `unit` is null for three different reasons, and only this one tells the
   * heading to say "Mixed Units". Null when the unit is not shown to the
   * reader; absent from an older API, which reads the same way.
   */
  unitMixed?: boolean | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

export interface SalesExplorerResponse {
  /** Members at this level BEFORE the top-500 cut — "top 500 of 4,213". */
  totalRows: number
  /**
   * The drill chain the procedure actually USED, level 1 first -- which is
   * what the grid labels itself from. Null (or absent, from an older API) on
   * an empty level, when the client falls back to the chain it asked for.
   */
  drillChain?: SalesDrillLevel[] | null
  rows: SalesExplorerRow[]
}

/** One line of one leaderboard; board is e.g. "customer-sales", "item-gp". */
export interface SalesBoardRow {
  board: string
  rank: number
  member: string | null
  value: number | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

export interface SalesTrendRow {
  year: number
  month: number
  sales: number | null
  gp: number | null
  lines: number
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

export interface SalesTreeNode {
  availableMeasures: string
  measure: string
  groupedBy: string
  nodeKey: string | null
  value: number | null
  sales: number | null
  gp: number | null
  gpPct: number | null
  lines: number
  /** What THIS BAR is in; null when the node spans several currencies. */
  currencyCode: string | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

export interface SalesTreeResponse {
  /** What was actually drawn — the server corrects an over-reaching request. */
  measure: string
  /** What this caller may analyse by; the client renders this menu and no other. */
  availableMeasures: string[]
  groupedBy: string
  nodes: SalesTreeNode[]
}

/* ---------------------------------------------------------------------------
   INVOICE LINES — the posted sales ledger, one row per line. Requested by
   management as a spreadsheet; see db/platform/022_sales_lines.sql for the
   column mapping and the totals it reproduces.
   --------------------------------------------------------------------------- */

/** One posted line. A masked money column is null, never zero. */
export interface SalesLine {
  companyCode: string | null
  company: string | null
  businessUnit: string | null
  businessTeam: string | null
  companyRegion: string | null
  customerRegion: string | null
  salesperson: string | null
  customerNumber: string | null
  customer: string | null
  itemNumber: string | null
  product: string | null
  supplier: string | null
  /** The Klüber reporting code (COMPANYCHAINID); most lines carry none. */
  kluberCode: string | null
  location: string | null
  district: string | null
  /** 'Invoice' or 'Return' — a return carries negative money. */
  docType: string | null
  docNumber: string | null
  batch: string | null
  invoiceDate: string | null
  uom: string | null
  qty: number | null
  sales: number | null
  cost: number | null
  charges: number | null
  gp: number | null
  /** A fraction, not a percentage: 0.2363 is 23.63%. */
  gpPct: number | null
  /** The line's own functional currency. One line belongs to one company, so
      this is never ambiguous — which is why the grid shows codes where the
      totals strip above it cannot. */
  currency: string | null
}

/** Totals over the WHOLE filtered set — never just the page on screen. */
export interface SalesLineTotals {
  qty: number | null
  sales: number | null
  cost: number | null
  charges: number | null
  gp: number | null
  gpPct: number | null
  /**
   * Under the new permission model, the totals that leave out lines whose figure is
   * withheld, as their names here, comma-separated; null when none does, and always
   * under today's model (M8; answer.ts).
   */
  partial?: string | null
  /* NO unit or currency on the TOTALS, deliberately. Answering it needed four
     MIN/MAX window aggregates over the whole filtered set, and MIN/MAX on
     nvarchar pays a collation comparison per row — the same shape cost the KPI
     head +1,799ms. This is the paged detail read, re-run on every sort and page
     turn. Each LINE carries its own `currency` instead, which is a plain column
     read and is where a reader looks anyway. */
}

export interface SalesLinesResponse {
  /** Lines in the whole filtered set, before paging. */
  totalRows: number
  page: number
  pageSize: number
  totals: SalesLineTotals
  rows: SalesLine[]
}

/** What the grid can be ordered by — the RESULT names the procedure accepts. */
export type SalesLineSort =
  | 'InvoiceDate' | 'Qty' | 'Sales' | 'Cost' | 'Charges' | 'Gp' | 'GpPct'
  | 'Customer' | 'Product' | 'Salesperson' | 'Supplier' | 'DocNumber'
  | 'Batch' | 'District' | 'DocType'

/** The Invoice Lines page's own option lists, scoped like the standing bar's. */
export interface SalesLineFilterRow {
  /** 'region' and 'supplier' left on 10 Sep 2026 — they are standing filters now. */
  /* 'chain' is the new model's name for 'kluber' (057, part 14b): the company
     chain the line belongs to. The page offers both under one menu. */
  kind: 'docType' | 'district' | 'location' | 'kluber' | 'chain'
  code: string | null
  label: string | null
}

/** The page's own narrowing, beside the report-level filters. */
export interface SalesLineFilters {
  docType?: string
  district?: string
  location?: string
  kluber?: string
  search?: string
  /** Management's own filter, on by default: a zero-quantity line is a correction. */
  hideZeroQty?: boolean
}

export interface SalesFilterRow {
  /** 'year' feeds the custom-range Year picker — the years the caller's scope actually contains. */
  kind: 'company' | 'businessUnit' | 'businessTeam' | 'salesperson' | 'supplier' | 'region' | 'year'
  code: string | null
  label: string | null
}

/**
 * One report update on the News page.
 *
 * THE FEED IS ALREADY FILTERED when it arrives. plat.usp_List_Report_News_MBI joins
 * through plat.fn_Openable_Reports_MBI, so a card about a report this caller may not
 * open never becomes a row. The client must not re-filter, and must not assume
 * it could: a headline is a disclosure in its own right, which is exactly why
 * the decision is taken in the database rather than here.
 */
export interface NewsItem {
  newsId: number
  /** The report this is about. The News page maps it to a PageId for "Open …". */
  reportSlug: string
  /** Shown as the card's eyebrow. */
  reportDisplayName: string
  headline: string
  summary: string
  /** Revealed on expand. PLAIN TEXT — rendered with whitespace-pre-wrap, never as HTML. */
  details: string | null
  /** An edit does not move this, so a corrected card keeps its place in the feed. */
  publishedUtc: string
  expiresUtc: string
  /** Only ever true under "show past updates", which is System Admin only. */
  isRetired: boolean
  isExpired: boolean
}

/** Publish a report update. System Admin only. */
export interface PublishNewsRequest {
  reportSlug: string
  headline: string
  summary: string
  details?: string | null
  /** Defaults to 30 server-side; the database bounds it to 1..365. */
  durationDays?: number
}

/** Correct a published update. The publish and expiry dates do not move. */
export interface UpdateNewsRequest {
  reportSlug: string
  headline: string
  summary: string
  details?: string | null
}

/* ---------------------------------------------------------------- material planning
   Five screens over four sources. A masked figure is null, never zero: the
   procedures mask before they aggregate, so "not shown to you" and "nothing"
   stay distinguishable all the way to the screen. Render null as an em dash.
   ------------------------------------------------------------------------------- */

/** Which purchase orders the screen is showing. */
export type MpOrderStatus = 'open' | 'review' | 'closed' | 'cancelled' | 'all'
/** How the stock grid is ordered. */
/**
 * The stock grid's sort.
 *
 * The four named values are the "Sort by" dropdown's, kept because the control
 * still offers them and 'onhand' is the report's default. Since 20 Sep 2026
 * every column heading sorts as well, and those send the column's own name —
 * hence the widening. The procedure holds the real list and silently falls
 * back to on-hand for anything it does not recognise, so a wrong string here
 * costs an unexpected order, never an error or a wrong permission.
 */
export type MpStockSort = 'onhand' | 'pallets' | 'movement' | 'item' | (string & {})
/**
 * The Inventory Position bookmark navigator. 'item' is the detail grain — one
 * row per item and warehouse — and stays the default. The SHAPE never changes
 * with the grain: at a coarser one the columns that no longer identify
 * anything come back null and `groupCode`/`groupLabel` say what was grouped.
 */
export type MpStockGroupBy = 'item' | 'product' | 'company' | 'bu' | 'bt'
/** Which shipment screen is asking. All three read one procedure. */
export type MpShipmentView = 'tracker' | 'arriving-soon' | 'late'
/** The two Overview breakdowns: the Power BI donut and its bar chart. */
export type MpBreakdownKind = 'cost-by-unit' | 'sales-qty-by-company'

/** The kinds the standing filter bar is built from. */
export type MpFilterKind =
  | 'company' | 'businessUnit' | 'itemGroup' | 'warehouse' | 'supplier'
  | 'modeOfDelivery' | 'supplierCategory' | 'shipmentStatus' | 'orderProgress'
  | 'orderStatus'
  | 'year'

/** One option for the standing filter bar, scoped to what the caller's rows contain. */
export interface MpFilterRow {
  kind: MpFilterKind
  code: string | null
  label: string | null
}

/**
 * The quantity tiles — the Power BI cards, card for card. A masked figure is
 * null; quantities an empty scope would blank are ISNULL'd in the procedure,
 * so a null quantity here can only be a mask (and never is, for a reader who
 * can open the report at all — see the page).
 */
export interface MpOverview {
  /** Newest invoice date in the caller's OWN rows — how fresh their data is. */
  freshThrough: string | null
  /** The "Inventory Quantity" card. */
  onHand: number | null
  onOrder: number | null
  /** Reserved physical — the report's "Open SO Qty". */
  reserved: number | null
  /** The "Open PO Qty" card: quantity where MNDFILECLOSED = 0. */
  openPoQty: number | null
  /** The "Open PO Count" card: distinct orders where MNDFILECLOSED = 0. */
  openPoCount: number | null
  /** The "YTD Qty Sold" card: SIGNED, this calendar year. */
  ytdQtySold: number | null
  /** Items whose on-hand is below Min_Inventory_Qty. */
  belowMinItems: number | null
  /** Units short across those items. */
  belowMinShortfall: number | null
  /** Cost_Inv, the report's own "Inventory Value" measure. Null without Inv_Cost. */
  inventoryValue: number | null
  /** The manager's "Item Count" card: distinct items in reach under the filter. */
  itemCount: number | null
  /** RESERVORDERED — the manager's "Total Reserved Quantity". NOT `reserved` above. */
  openSoQty: number | null
  /** The manager's "Total PO Quantity": quantity ordered this year. */
  poQtyYtd: number | null
  /** The same, the year before, so the card carries its own comparison. */
  poQtyPY: number | null
  /**
   * WHAT THESE FIGURES ARE IN. `unit` is the unit of measure when the group
   * has one and null when it has several; `unitCount` says which. Above one,
   * the quantity adds unlike things — 37 of 43 companies and 116 of 226
   * business units are in that state — so the screen says "mixed" rather than
   * printing one member's unit over a sum. Currency works the same way, and is
   * always 'USD' with a count of 1 in USD mode.
   */
  unit: string | null
  unitCount: number | null
  currencyCode: string | null
  currencyCount: number | null
  /**
   * WHETHER THE READER MAY SEE IT — not whether any of it exists.
   *
   * Every masked measure on this report arrives as `null`, so that a figure
   * somebody is not permitted to see can never render as a confident zero.
   * "Nothing was recorded" arrives as `null` too, and from the values alone the
   * two are indistinguishable — so the screen used to guess, with "every value
   * is null, therefore masked".
   *
   * That guess told a System Admin holding full bypass that a company with no
   * stock meant he lacked the inventory-cost permission. Both halves were
   * wrong. The procedure computes these flags for its own masking, so it states
   * them and the screen reports the real reason.
   */
  canSeeCost: boolean
  canSeeQtySold: boolean
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** One slice of an Overview breakdown. `valuePY` is null on the cost breakdown. */
export interface MpBreakdownRow {
  label: string | null
  code: string | null
  value: number | null
  valuePY: number | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** One item in one warehouse — every column of Inventory Position and Warehouse Capacity. `totalRows` counts every page. */
export interface MpStockRow {
  totalRows: number
  company: string | null
  companyName: string | null
  itemNumber: string | null
  productName: string | null
  itemGroup: string | null
  /** The unit row security matches on. */
  businessUnit: string | null
  /** Display only — this one matches no grant. */
  businessUnitName: string | null
  businessTeam: string | null
  warehouse: string | null
  /** Null on a row with no inventory record at all (55% of production; hidden unless asked for). */
  onHand: number | null
  physicalInv: number | null
  onOrder: number | null
  reserved: number | null
  /** The LEGACY minimum, set on 98 production rows. Not what the screen calls Minimum. */
  minStock: number | null
  /** Null where no pallet size is recorded — never zero, or a warehouse looks emptier than it is. */
  pallets: number | null
  inventoryCost: number | null
  inventoryValue: number | null
  unitCost: number | null
  /** Min_Inventory_Qty — THE minimum. */
  minInventory: number | null
  /** The report's IPQV2: reserved − on hand + on order. */
  position: number | null
  /** Distinct open orders for the item (MNDFILECLOSED = 0). A count: never null. */
  openPoCount: number
  palletSize: number | null
  /** Null on 75% of stocked rows — a date nobody recorded is not "today". */
  lastMovement: string | null
  daysSinceMovement: number | null
  businessTeamName: string | null
  /** RESERVORDERED: reserved against stock still ON ORDER. A different column from `reserved`. */
  openSoQty: number | null
  /**
   * The manager's Difference: open SOs − (on hand + on order). POSITIVE means
   * short — the opposite sign convention from `position` beside it, which is
   * the old Power BI IPQV2 and ADDS what is on order. Both are here so nobody
   * has to guess which number they are reading.
   */
  shortfall: number | null
  /** What the row is grouped by, at whatever grain was asked for. */
  groupCode: string | null
  /** The name of that group, so the grid can label a column it did not choose. */
  groupLabel: string | null
  /** The product master's vendor. Null above item grain: a supplier belongs to an item. */
  supplier: string | null
  /**
   * WHAT THESE FIGURES ARE IN. `unit` is the unit of measure when the group
   * has one and null when it has several; `unitCount` says which. Above one,
   * the quantity adds unlike things — 37 of 43 companies and 116 of 226
   * business units are in that state — so the screen says "mixed" rather than
   * printing one member's unit over a sum. Currency works the same way, and is
   * always 'USD' with a count of 1 in USD mode.
   */
  unit: string | null
  unitCount: number | null
  currencyCode: string | null
  currencyCount: number | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** One purchase-order line — every column the three Power BI order pages bound. */
export interface MpPurchaseOrderRow {
  totalRows: number
  company: string | null
  poNumber: string | null
  lineNumber: number | null
  itemId: string | null
  itemName: string | null
  supplier: string | null
  site: string | null
  currencyCode: string | null
  poDate: string | null
  deliveryDate: string | null
  etaDate: string | null
  grnDate: string | null
  /** 1 and 2 are open, 3 finished, 4 cancelled. */
  lineStatusCode: number | null
  /** 0 Draft · 10 In Review · 20 Rejected · 30 Approved · 35 transitional · 40 Confirmed · 50 Finalized. */
  approvalCode: number | null
  /** The ERP's own label. Independent of the line status, not derived from it. */
  orderStatus: string | null
  /** Where the goods physically are. */
  shipmentProgress: string | null
  /** The flag the headline tiles define "open" by; null on 47.5% of lines. */
  fileClosed: number | null
  quantity: number | null
  /** Null on roughly half the lines in production, and null means UNKNOWN, not zero. */
  stillDue: number | null
  unitPrice: number | null
  priceBasis: number | null
  lineAmount: number | null
  etdDate: string | null
  shipDate: string | null
  shipDate2: string | null
  readinessDate: string | null
  deliveryTerms: string | null
  mawb: string | null
  modeOfDelivery: string | null
  responsible: string | null
  orderProgress: string | null
  shipmentStatus: string | null
  supplierCategory: string | null
  /** The LINE's own unit of measure, not the product master's — they differ on 1,857 of 33,476 production lines. */
  unit: string | null
  /**
   * THE BOTTOM ROW, over EVERY line that matches the filters -- not the page
   * on screen. The same on every row; read it from the first. A figure is null
   * only when masked. Its label (unit, currency) is null when there is no single
   * answer -- several units or currencies, or a line missing its unit, figure or
   * rate -- and the screen then says so instead of printing a number.
   */
  totalQty: number | null
  totalQtyUnit: string | null
  totalAmount: number | null
  totalAmountCurrency: string | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/**
 * One line on the Shipment Tracker, Arriving Soon or Late Shipments. The three
 * screens differ only in their date rule, so they share this shape.
 */
export interface MpShipmentRow {
  totalRows: number
  company: string | null
  poNumber: string | null
  lineNumber: number | null
  itemId: string | null
  itemName: string | null
  supplier: string | null
  poDate: string | null
  /** The page's sort key, newest first. */
  readinessDate: string | null
  etdDate: string | null
  etaDate: string | null
  shipDate: string | null
  shipDate2: string | null
  grnDate: string | null
  mawb: string | null
  modeOfDelivery: string | null
  orderProgress: string | null
  shipmentStatus: string | null
  currencyCode: string | null
  orderStatus: string | null
  responsible: string | null
  lineStatusCode: number | null
  quantity: number | null
  stillDue: number | null
  unitPrice: number | null
  priceBasis: number | null
  /** The manager's "PO Value", converted per row. Null without Purch_Cost. */
  lineAmount: number | null
  /** Who raised it — Created_By, populated on 31,703 lines across 433 people. */
  orderer: string | null
  deliveryTerms: string | null
  site: string | null
  approvalCode: number | null
  /** The LINE's own unit of measure, not the product master's — they differ on 1,857 of 33,476 production lines. */
  unit: string | null
  /**
   * THE BOTTOM ROW, over EVERY line that matches the filters -- not the page
   * on screen. The same on every row; read it from the first. A figure is null
   * only when masked. Its label (unit, currency) is null when there is no single
   * answer -- several units or currencies, or a line missing its unit, figure or
   * rate -- and the screen then says so instead of printing a number.
   */
  totalQty: number | null
  totalQtyUnit: string | null
  totalAmount: number | null
  totalAmountCurrency: string | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** One month beside the same month a year earlier. Always twelve of them. */
export interface MpSalesMonth {
  monthNumber: number
  monthStart: string | null
  sales: number | null
  salesPY: number | null
  /** SIGNED — a credit note subtracts, matching the Sales report. */
  qty: number | null
  qtyPY: number | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** One product on Sales Performance: ABSOLUTE quantity sold, this year against last. */
export interface MpSalesPerformanceRow {
  totalRows: number
  company: string | null
  companyName: string | null
  itemNumber: string | null
  productName: string | null
  businessTeam: string | null
  supplier: string | null
  /** Absolute — a credit note ADDS here, as the Power BI measure did. Null without Qty_Sold. */
  qtySold: number | null
  qtySoldPY: number | null
  qtyDiff: number | null
  /** Null when there is no prior year to compare against. */
  variancePct: number | null
  /** The item's unit of measure. An item never carries two. */
  unit: string | null
  /**
   * Whether the caller may see sales quantity — NOT whether any was sold. A
   * property of the caller, so it is the same on every row; the screen reads it
   * off the first. This surface fetches nothing but its own rows, so unlike the
   * Overview's charts it has nothing else to cross-check against, which is why
   * the procedure carries the flag itself.
   */
  canSeeQtySold: boolean
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** One item on the Item Status grid: the Company Overview pivot's columns. */
export interface MpItemStatusRow {
  totalRows: number
  company: string | null
  itemNumber: string | null
  productName: string | null
  businessUnit: string | null
  itemGroup: string | null
  onHand: number | null
  onOrder: number | null
  /** The legacy minimum; not what the screen calls Minimum. */
  minStock: number | null
  /** On hand minus Min_Inventory_Qty; null when no minimum is set. */
  belowMinimum: number | null
  inventoryValue: number | null
  /** Quantity on open orders, MNDFILECLOSED = 0 — the same definition as the Overview tile. */
  poQty: number | null
  /** ABSOLUTE quantity sold in the selected year. */
  qtySold: number | null
  supplier: string | null
  /** Min_Inventory_Qty — THE minimum. */
  minInventory: number | null
  reserved: number | null
  /** A count: never null. */
  openPoCount: number
  qtySoldPY: number | null
  qtyDiff: number | null
  inventoryCost: number | null
  /** The Company Overview's "PO YTD": quantity ordered in the selected year. */
  poQtyYtd: number | null
  /** "PO PY" — the year before. */
  poQtyPY: number | null
  /** "Diff" — PO YTD less PO PY, as the manager wrote it. */
  poQtyDiff: number | null
  /** "Open SO YTD": RESERVORDERED. */
  openSoQty: number | null
  /** The manager's second Diff: sold YTD plus open SOs, less sold PY. */
  netSoldDiff: number | null
  /** The item's unit of measure. An item never carries two. */
  unit: string | null
  /** The company's currency, or 'USD' in USD mode. One item, one company, so always single. */
  currencyCode: string | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/**
 * One open order behind an On Order figure — the tooltip from the manager's
 * notes. Ordered by ETA, nulls last, because the question it answers is
 * "when does this arrive".
 */
export interface MpItemOrderRow {
  poNumber: string | null
  /** Its line, so two lines of one order stay distinct. */
  lineNumber: number | null
  company: string | null
  supplier: string | null
  quantity: number | null
  /** Null means UNKNOWN, not zero. */
  stillDue: number | null
  etaDate: string | null
  /** Converted per row. Null without Purch_Cost. */
  lineAmount: number | null
  /** The LINE's own unit of measure, not the product master's — they differ on 1,857 of 33,476 production lines. */
  unit: string | null
  /** The line's own currency, or 'USD' in USD mode. Two lines of one item may differ. */
  currencyCode: string | null
}

/** The Item Status lookup: one row for the selection, a year against the year before. */
export interface MpItemSummary {
  year: number
  onHand: number | null
  /** Physical_Inventory — the page's "Inventory On Hand". */
  physicalInv: number | null
  onOrder: number | null
  /** Physical plus on order — the page's "Inventory Total Qty". */
  totalQty: number | null
  reserved: number | null
  poQtyYear: number | null
  poQtyPY: number | null
  poQtyDiff: number | null
  poValueYear: number | null
  poValuePY: number | null
  poValueDiff: number | null
  /** SIGNED. */
  salesQtyYear: number | null
  salesQtyPY: number | null
  salesQtyDiff: number | null
  salesValueYear: number | null
  salesValuePY: number | null
  salesValueDiff: number | null
  /** Cost_Inv. Null without Inv_Cost. */
  inventoryValue: number | null
  /**
   * WHAT THESE FIGURES ARE IN. `unit` is the unit of measure when the group
   * has one and null when it has several; `unitCount` says which. Above one,
   * the quantity adds unlike things — 37 of 43 companies and 116 of 226
   * business units are in that state — so the screen says "mixed" rather than
   * printing one member's unit over a sum. Currency works the same way, and is
   * always 'USD' with a count of 1 in USD mode.
   */
  unit: string | null
  unitCount: number | null
  currencyCode: string | null
  currencyCount: number | null
  /**
   * Under the new permission model, the figures here that leave out values hidden
   * from this reader, as their names, comma-separated; null when none does, and
   * always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/* ---------------------------------------------------------------- inventory aging
   Three screens over one stock source (039/040, 4 Oct 2026). Quantities follow
   the report's own gate and are never masked for anybody who can open it;
   money follows Inv_Cost and arrives null without it — and `canSeeCost` on the
   overview says which, so the screen never infers a mask from a null.
   ------------------------------------------------------------------------------- */

/**
 * The Aging screen's Group-by picker. One row shape at every grain.
 * 'productwh' (5 Oct 2026) is a product in one warehouse — Power BI V1's
 * product page, which listed a product once per warehouse.
 */
export type InvGroupBy = 'company' | 'bu' | 'bt' | 'product' | 'productwh' | 'lot'

/**
 * THE BRANCH A DRILL-DOWN ROW OPENS (5 Oct 2026): Company -> Unit -> Team ->
 * Product -> Lot. `unitNone`/`teamNone` name the BLANK member — items with no
 * business unit in D365 (1,243 lots on the clone) — because an absent `unit`
 * already means "no unit chosen yet", and a blank branch that cannot say so
 * is a drill that cannot advance.
 */
export interface InvDrillPath {
  company?: string
  unit?: string
  unitNone?: boolean
  team?: string
  teamNone?: boolean
  item?: string
}
/**
 * The Expiry screen's window. Every "within" window counts TODAY as its first
 * day; expired is before today. The three past 180 days (5 Oct 2026, the
 * Operations report's Expiry Period bands) are BANDS: 181–240, 241–365 and
 * over 365 days ahead.
 */
export type InvWindow =
  | 'all' | 'expired' | 'within30' | 'within90' | 'within180'
  | 'months6to8' | 'months8to12' | 'over12months' | 'noexpiry'
/** The kinds the standing filter bar is built from. */
export type InvFilterKind = 'company' | 'businessUnit' | 'businessTeam' | 'warehouse'
/** Age bands, as the procedures name them. 'drop' and 'unknown' are in no age bucket. */
export type InvAgeBand =
  | '0_30' | '31_60' | '61_90' | '91_120' | '121_180' | '181_240' | '241_365' | 'over365' | 'drop' | 'unknown'
/** Expiry bands. 'expired' is before today; 'none' is no expiry date at all. */
export type InvExpiryBand =
  | 'expired' | '0_30' | '31_60' | '61_90' | '91_120' | '121_180' | '181_240' | '241_365' | 'over365' | 'none'

/** One option for the standing filter bar, scoped to what the caller's rows contain. */
export interface InvFilterRow {
  kind: InvFilterKind
  code: string | null
  label: string | null
}

/** Every tile on the three screens, in one row. */
export interface InvOverview {
  lots: number | null
  items: number | null
  companies: number | null
  /** Total quantity, drop-ship included. */
  quantity: number | null
  /** Total value, drop-ship included. Null without Inv_Cost. */
  value: number | null
  /** Arrived 181+ / 241+ / 366+ days ago, drop-ship excluded — the Power BI "Over N Days" cards. */
  qtyOver180: number | null
  qtyOver240: number | null
  qtyOver365: number | null
  valueOver180: number | null
  valueOver240: number | null
  valueOver365: number | null
  /** In drop-ship warehouses, which no age bucket holds. */
  qtyDrop: number | null
  valueDrop: number | null
  /** No arrival date, or one in the future. */
  unknownAgeLots: number | null
  /** Expiry before today — the 1 Jan 1900 placeholder included, as Power BI counted it. */
  expiredLots: number | null
  expiredQty: number | null
  expiredValue: number | null
  /** Expiring today through day 90. */
  within90Lots: number | null
  within90Qty: number | null
  within90Value: number | null
  /** Expiring today through day 180. */
  within180Lots: number | null
  within180Qty: number | null
  within180Value: number | null
  /** No expiry date at all. */
  noExpiryLots: number | null
  /** WHAT THE FIGURES ARE IN: one unit / currency, or null with a count above one. */
  unit: string | null
  unitCount: number | null
  currencyCode: string | null
  currencyCount: number | null
  /** Whether this reader may see money at all — not whether any exists. */
  canSeeCost: boolean
  /**
   * Under the new permission model, the tiles that leave out values hidden
   * from this reader, as their names here, comma-separated; null when none
   * does, and always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/**
 * One group on the Aging screen. Identity columns that do not identify a group
 * at the requested grain are null. Buckets come in quantity (q…) and value (v…);
 * the eight age buckets plus drop-ship plus unknown add up to the total.
 */
export interface InvMatrixRow {
  totalRows: number
  groupCode: string | null
  groupLabel: string | null
  company: string | null
  companyName: string | null
  businessUnit: string | null
  businessTeam: string | null
  itemNumber: string | null
  productName: string | null
  lot: string | null
  warehouse: string | null
  lots: number
  quantity: number | null
  value: number | null
  /** Value ÷ quantity, never a sum of unit costs. Null above the item when the group holds several units. */
  unitCost: number | null
  q0_30: number | null
  q31_60: number | null
  q61_90: number | null
  q91_120: number | null
  q121_180: number | null
  q181_240: number | null
  q241_365: number | null
  qOver365: number | null
  qDrop: number | null
  qUnknown: number | null
  v0_30: number | null
  v31_60: number | null
  v61_90: number | null
  v91_120: number | null
  v121_180: number | null
  v181_240: number | null
  v241_365: number | null
  vOver365: number | null
  vDrop: number | null
  vUnknown: number | null
  oldestArrival: string | null
  earliestExpiry: string | null
  /** On lot rows only — never summed up a level. */
  daysOld: number | null
  daysToExpire: number | null
  unit: string | null
  unitCount: number | null
  currencyCode: string | null
  currencyCount: number | null
  /** The product's description, on product, product-by-warehouse and lot rows; null above them. */
  description: string | null
  /**
   * Under the new permission model, the row's figures that leave out values hidden
   * from this reader, as their names here, comma-separated; null when none
   * does, and always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** One lot: an item, in one lot number, in one warehouse, in one company. */
export interface InvLotRow {
  totalRows: number
  company: string | null
  companyName: string | null
  businessUnit: string | null
  businessTeam: string | null
  itemNumber: string | null
  productName: string | null
  description: string | null
  supplier: string | null
  lot: string | null
  warehouse: string | null
  unit: string | null
  mfgDate: string | null
  arrivalDate: string | null
  /** 1 Jan 1900 is a placeholder, and counts as expired. */
  expiryDate: string | null
  bestBefore: string | null
  shelfAdvice: string | null
  /** Negative when the arrival is in the future. */
  daysOld: number | null
  /** Negative once expired. */
  daysToExpire: number | null
  ageBand: InvAgeBand | null
  expiryBand: InvExpiryBand | null
  /** Negative stock is real, and shown. */
  quantity: number | null
  unitCost: number | null
  value: number | null
  currencyCode: string | null
}

/* ---------------------------------------------------------- statement of account
   A statement to send to a customer, Full or Open, as the D365 team specified
   (042/043 version 2, 6 Oct 2026). Every figure follows AR_Mod and arrives null
   without it, never zero. Amounts are in each transaction's own currency, one
   section per company and currency; nothing converts.
   ------------------------------------------------------------------------------- */

/** Full: every transaction in the period. Open: what is unpaid today, dated up to To. */
export type StatementKind = 'full' | 'open'

/** One company the reader can see a customer in: the Company box's options. */
export interface StatementCompany {
  /** Upper case, e.g. R021. */
  company: string | null
  /** As D365 prints it on its letterhead. */
  companyName: string | null
  /** How many of its customer accounts the reader can see. */
  customers: number
}

/** One account of one customer record the reader may produce a statement for. */
export interface StatementCustomer {
  /** How many customer records matched in all; at most 50 come back. */
  totalMatches: number
  /** D365's customer record: the same in every company the customer buys from. */
  party: string | null
  /** Upper case, e.g. R021. */
  company: string | null
  /** As D365 prints it on its letterhead. */
  companyName: string | null
  customerAccount: string | null
  /** Arabic for about a third of customers. */
  customerName: string | null
}

/** One company section's heading: the letterhead and the customer, as the D365 templates print them. */
export interface StatementHeader {
  company: string | null
  companyName: string | null
  customerAccount: string | null
  customerName: string | null
  /** TODAY's terms, as D365 holds them (e.g. "090 Days"); null when withheld. */
  paymentTerms: string | null
  /** The company's own currency, whose section prints first. */
  homeCurrency: string | null
  party: string | null
  kind: StatementKind
  /** The period as the server applied it (ISO). */
  fromDate: string | null
  toDate: string | null
  /** The server's today: what "unpaid as of today" means. */
  today: string | null
  /** The company's letterhead address, its lines split by "\n". */
  companyAddress: string | null
  /** Where D365 holds one (14 and 7 of 41 companies, 6 Oct 2026). */
  companyPhone: string | null
  companyFax: string | null
  /** The customer's primary address as D365 holds it: addressLines() cleans it. */
  customerAddress: string | null
  /** Today's limit in the company's own currency; 0 when none is set; null when withheld. */
  creditLimit: number | null
  /** The limit minus the balance at the To date, company currency; null when withheld. */
  creditAvailable: number | null
}

/** One row of the statement: a section's opening balance, a line, or its closing row. */
export interface StatementLine {
  company: string | null
  currency: string | null
  rowKind: 'opening' | 'line' | 'closing'
  seq: number
  lineDate: string | null
  /** The customer's salesperson. */
  salesMan: string | null
  /** The invoice number, else the voucher. */
  document: string | null
  /** For invoices. */
  salesOrder: string | null
  /** The customer's reference for an invoice, the cheque reference for a payment. */
  reference: string | null
  dueDate: string | null
  /** To date minus the due date; null where the line has no due date. */
  overdueDays: number | null
  /** The Open statement's full line amount; null on Full. */
  originalAmount: number | null
  /** Charged (Open: still owed); on the closing row, the period's total. */
  debit: number | null
  /** Paid or credited, as a POSITIVE figure; on the closing row, the total. */
  credit: number | null
  /** Signed: what was owed after this row. */
  balance: number | null
  /**
   * Under the new permission model, the figures on this row that leave out values
   * hidden from this reader, as their names here, comma-separated; null when none
   * does, and always under today's model (M8; answer.ts).
   */
  partial?: string | null
}

/** The whole statement, from one request. */
export interface StatementResponse {
  /** One per company section, in company order. */
  headers: StatementHeader[]
  lines: StatementLine[]
}

/* ===========================================================================
   DEVELOPER MODE (028)

   What every figure on a report means, for the people who can judge whether it
   is right. System Admin only, and off until somebody turns it on.
   =========================================================================== */

/** The four switches. `dev_mode` is the master; the other three decide sections. */
export type DevFlagKey = 'dev_mode' | 'dev_show_source' | 'dev_show_math' | 'dev_show_query'

export interface DevFlag {
  key: DevFlagKey
  enabled: boolean
  /** Null when nobody has ever moved it. */
  changedUtc: string | null
  /** Who last moved it — the accountability half of the switch. */
  changedBy: string | null
}

export interface SetDevFlagRequest {
  key: DevFlagKey
  enabled: boolean
  /** Mandatory. The database refuses a blank one, not the browser. */
  reason: string
}

/**
 * What one figure means.
 *
 * EVERY NULLABLE FIELD IS AMBIGUOUS ON PURPOSE. A section is absent either
 * because nothing was documented for that key or because its switch is off, and
 * the browser is told neither — the server strips what the flags do not permit
 * BEFORE sending, so an absent section was never on the wire. Rendering has to
 * treat both the same way, which is also the honest way: "not shown to you now".
 */
/**
 * One object a figure is read from, and how it attaches to what came before.
 *
 * A LIST rather than two strings, because two strings only ever worked on a
 * report where every measure reads one view. Material Planning's figures span
 * four objects, and a flat comma-separated list of columns across four tables is
 * a word search rather than an answer: a reviewer needs to know which columns
 * came from which object, and on what condition they were joined.
 */
export interface MeasureSource {
  /** Reading order. 1 is the fact the figure is measured over. */
  ordinal: number
  /** The view, table or function, schema-qualified. */
  object: string
  /**
   * 'fact', 'dimension', 'security' or 'options'. Said out loud, because "this
   * one decides WHO sees the row" is a different kind of fact from "this one
   * supplies the product name", and a reviewer has to be able to tell them
   * apart.
   *
   * 'options' belongs to a filter: the object its MENU was built from, which is
   * usually the same view read by a different procedure. It is the row that
   * tells a reviewer the menu is SCOPED — so two people comparing their
   * supplier lists and finding them different have found the design, not a bug.
   * It is also the one role with no join, because an option list does not
   * attach to anything.
   */
  role: string
  /** The columns taken from THIS object, and only this one. */
  columns: string | null
  /**
   * Null on the fact; present on everything after it. Written out in full —
   * LEFT versus INNER is the difference between a missing dimension row leaving
   * a blank name and deleting the figure entirely.
   */
  joinKind: string | null
  joinTo: string | null
  joinOn: string | null
  note: string | null
}

/**
 * What kind of thing was clicked, and therefore what the panel should ask.
 *
 * - `measure`   arithmetic happened. "How it is calculated."
 * - `dimension` carried through from a column untouched. Saying "how it is
 *               calculated" over a supplier name would describe something the
 *               platform never did.
 * - `filter`    a control, not a figure. "How it MATCHES" — which column, and
 *               whether that match is an equals, a contains or a one-of. That
 *               distinction is the one the screen has never stated and the one
 *               people get wrong.
 */
export type MeasureKind = 'measure' | 'dimension' | 'filter'

/**
 * A documented thing, named but not explained.
 *
 * WHY THE MANIFEST EXISTS. Without it a page must either make every header
 * clickable — most clicks then landing on "nothing documented", which reads as
 * a fault and is indistinguishable from a failure to load — or carry its own
 * hard-coded list of documented keys, which is the same list written twice.
 * Asking the server means documenting a column is a one-file change that lights
 * its header up on its own.
 *
 * It carries no definition, no formula and no source. Those still arrive one at
 * a time, still section-filtered, still audited per open.
 */
export interface MeasureRef {
  surface: string
  key: string
  kind: MeasureKind
}

/**
 * The full explanation of one thing.
 *
 * It carries no `kind`: the panel takes that from the manifest it already holds,
 * so there is one place the answer comes from rather than two that could drift.
 */
export interface MeasureDoc {
  surface: string
  key: string
  displayName: string
  /** Always present. The words are the point; everything else supports them. */
  definition: string
  /**
   * Empty for BOTH "nothing documented" and "the source switch is off" — the
   * server strips what the flags do not permit before sending, so the two are
   * indistinguishable here on purpose.
   */
  sources: MeasureSource[]
  formula: string | null
  procName: string | null
  sourceFile: string | null
  notes: string | null
}

/* ===========================================================================
   THE ACCESS MAP'S SCREENS (new model; 05 section 6.7, Part 17)

   The API passes each procedure's columns through by name, camel-cased
   (Mindzer_User_Id -> mindzerUserId), and decides nothing about them: these
   types name what the screens read. Times are UTC ISO strings.
   =========================================================================== */

/** The one badge a person carries (sec.fn_Person_Status_MBI, 052): the first that holds. */
export type AccessPersonStatus =
  | 'SEES_ALL' | 'LEFT' | 'DISABLED' | 'PLACEHOLDER' | 'BAD_EMAIL' | 'NO_ROLE' | 'ROLE_NOT_SET'
  | 'RETURNED' | 'WIDENED' | 'CAN_ENTER' | 'NO_SIGN_IN' | 'WAITING'

/** One row of Access > People. */
export interface AccessPersonRow {
  mindzerUserId: number
  displayName: string | null
  email: string | null
  compId: string | null
  roleId: number | null
  roleName: string | null
  roleFunction: string | null
  status: AccessPersonStatus
  /** APPROVED, WITHDRAWN, or null: never decided. */
  approval: string | null
  decidedUtc: string | null
  warningCount: number
  personalChanges: number
  extraScopes: number
  /** 0 off, 1 type 1, 2 type 2. */
  hierarchyMode: number
  mobileAllowed: boolean
  lastSignInUtc: string | null
  /**
   * Whether their approval counts (17f; sec.v_Approval_In_Force_MBI). The badge cannot
   * say it: disabled, no HR role and the like come before "back after leaving" in
   * its order. Absent from an API before 17f.
   */
  approvalInForce?: boolean
}

export interface AccessPeopleFilter {
  search?: string
  status?: AccessPersonStatus
  company?: string
  role?: number
  warnings?: boolean
  personal?: boolean
  left?: boolean
  /** Only approvals in force (Approvals › Approved; 17f). */
  approved?: boolean
  /** Which page of 2,000, from 1 (17g; review R-20). */
  page?: number
}

export interface AccessPeople {
  people: AccessPersonRow[]
  /** How many active people Mindzer sends. */
  activeTotal: number | null
  /** How many match the search and filters, before the page (17g). Absent from an API before it. */
  listed?: number | null
}

/** The person's page, PERSON part. */
export interface AccessPerson {
  mindzerUserId: number
  displayName: string | null
  email: string | null
  emailStatus: string | null
  isActive: boolean
  isDisabled: boolean
  inSource: boolean
  compId: string | null
  entityName: string | null
  roleId: number | null
  roleName: string | null
  roleFunction: string | null
  roleSetUp: boolean
  status: AccessPersonStatus
  systemRole: string | null
  /** ALLOWED, a DENIED_* reason as the builder last worked it out, or NOT_BUILT. */
  entryNow: string
  approval: string | null
  decidedUtc: string | null
  decidedByEmail: string | null
  decisionReason: string | null
  decisionWarningCount: number | null
  returned: boolean
  firstSeenUtc: string | null
  signedIn: boolean
  lastSignInUtc: string | null
  hierarchyMode: number
  /** PERSON (their own setting), ROLE, or NONE. */
  hierarchyFrom: string
  mobileAllowed: boolean
  mobileFrom: string
  lineManagerId: number | null
  funcManagerId: number | null
  staffDirect: number
  staffAll: number
  warningCount: number
  /** What a personal-settings save quotes; null before their first. */
  settingsVersion: string | null
  /** The sign-in linked to them, which "Login As this person" acts as; null before their first. */
  signInOid: string | null
  /**
   * Whether their approval counts (17k; sec.v_Approval_In_Force_MBI), as on People's rows.
   * The badge cannot say it: "role not set up" and the like come first in its order.
   * Absent from a database before 17k.
   */
  approvalInForce?: boolean
}

/**
 * One report or column group in one scope: 0 is the main scope, else an extra scope's
 * ID. A group's code is its ID and its report is reportSlug (D53): the screens show a
 * group under its own report, and only where that report is in the scope.
 */
export interface AccessMatrixCell {
  kind: 'REPORT' | 'GROUP'
  code: string
  label: string
  scopeKey: number
  value: boolean
  source: 'ROLE' | 'ROLE_OFF' | 'PERSON_ON' | 'PERSON_OFF' | 'GIVEN' | 'NOT_GIVEN'
  /** The report: a report row's own slug, a group's report. */
  reportSlug: string
  /** A group's key as read (null on a report row): the person and additional scope saves send it back (51637). */
  groupKey: string | null
}

/** A Mindzer unit or team the person sits in, with the D365 code it links to. */
export interface AccessPlacement {
  kind: 'UNIT' | 'TEAM'
  mindzerId: number
  name: string | null
  d365Code: string | null
  unitId: number | null
}

/** One of their own main-scope lines: A adds, R removes. */
export interface AccessScopeEdit {
  editId: number
  effect: 'A' | 'R'
  compId: string | null
  businessUnit: string | null
  anyUnit: boolean
  businessTeam: string | null
  anyTeam: boolean
  inMainCompany: boolean
  pausedUtc: string | null
  pausedReason: string | null
  changedByEmail: string | null
  changeReason: string | null
  changedUtc: string | null
}

/** An extra scope in another company. */
export interface AccessExtraScope {
  extraScopeId: number
  compId: string
  businessUnit: string | null
  anyUnit: boolean
  businessTeam: string | null
  anyTeam: boolean
  /** Slugs, comma separated. */
  reports: string | null
  /** Column group IDs, comma separated (D53). */
  columnGroupIds: string | null
  pausedUtc: string | null
  pausedReason: string | null
  changedByEmail: string | null
  changeReason: string | null
  version: string | null
  /** The column groups' names, comma separated. */
  columnGroups: string | null
  /** The scope this line belongs to (its first line's ID): one card per scope (owner, 29 Oct 2026). */
  scopeGroup: number
}

/** One warning, as the approval card shows it. Severity, most serious first. */
export interface AccessWarning {
  warningCode: string
  detail: string
  sourceKind: string | null
  sourceRef: string | null
  severity: 'WIDER_ROWS' | 'BLOCKS_ENTRY' | 'NO_ROWS' | 'FEWER_ROWS' | 'INFO' | string
  /**
   * The approval card only (R-25): what a unit, team or code warning costs, as JSON:
   * {"Comp_Id": the company that uses the code, or null for their own; "Reports":
   * [{Report_Slug, Display_Name, Rows}]}, the last 12 months. Absent or null: nothing
   * to count (a team with no D365 code, or a warning that is not about rows).
   */
  rowsJson?: string | null
}

export interface AccessPersonPage {
  person: AccessPerson | null
  matrix: AccessMatrixCell[]
  placement: AccessPlacement[]
  edits: AccessScopeEdit[]
  extras: AccessExtraScope[]
  warnings: AccessWarning[]
}

/** One person waiting for approval (Access > Approvals). */
export interface AccessApprovalRow {
  mindzerUserId: number
  displayName: string | null
  email: string | null
  compId: string | null
  roleId: number | null
  roleName: string | null
  roleSetUp: boolean
  warningCount: number
  /** The most serious warning's severity, or null. */
  worstSeverity: string | null
  widens: boolean
  lastSignInUtc: string | null
  signedIn: boolean
  /** WITHDRAWN when an approval was taken back; else null. */
  decision: string | null
  decidedUtc: string | null
  returned: boolean
}

export interface AccessApprovalFilter {
  /** tried: waiting and tried to sign in; waiting: everyone undecided. */
  view: 'tried' | 'waiting'
  company?: string
  role?: number
  /** NONE, a severity such as WIDER_ROWS, or a warning code. */
  problem?: string
  /** Default yes, as the screen's filter. */
  roleSetUp?: 'yes' | 'no' | 'any'
  /** Part of a name or an email. */
  search?: string
  /** From 1. */
  page?: number
  /** Rows a page (500 by default); the two counts are the whole list's either way. */
  top?: number
}

export interface AccessApprovals {
  people: AccessApprovalRow[]
  /** The two counts, unfiltered. */
  tried: number | null
  waiting: number | null
  /** How many match the view, the filters and the search, before paging. */
  listed: number | null
}

/** The approval card's person. */
export interface AccessCardPerson {
  mindzerUserId: number
  displayName: string | null
  email: string | null
  emailStatus: string | null
  isActive: boolean
  isDisabled: boolean
  inSource: boolean
  excluded: boolean
  compId: string | null
  entityName: string | null
  roleId: number | null
  roleName: string | null
  roleFunction: string | null
  roleSetUp: boolean
  hierarchyMode: number
  mobileAllowed: boolean
  lineManagerId: number | null
  funcManagerId: number | null
  signedIn: boolean
  lastSignInUtc: string | null
  approval: string | null
  decidedUtc: string | null
  decidedByEmail: string | null
  approvedEmail: string | null
  returned: boolean
  entryNow: string
  warningCount: number
  blocksEntry: boolean
  widens: boolean
  /** Quoted by an approval: it covers every warning shown (51604 if they changed). */
  warningsKey: string
  decisionReason: string | null
}

/** "If you approve, they will see": one report on one scope line, with its rows. */
export interface AccessCardSees {
  reportSlug: string
  displayName: string
  scopeKey: number
  lineSource: string | null
  compId: string | null
  businessUnit: string | null
  anyUnit: boolean | null
  businessTeam: string | null
  anyTeam: boolean | null
  /** The column groups this scope gives on this report, by name, comma separated (D53). */
  columnGroups: string | null
  rowsSource: string | null
  /** Rows dated in the last 12 months it reaches (counts, never rows); null when unknown. */
  rowsRecent: number | null
}

/** What a type-2 manager would inherit; zeros otherwise. */
export interface AccessCardInherits {
  hierarchyMode: number
  staffWithScopes: number
  inheritedLines: number
  staffWithProblems: number
}

export interface AccessApprovalCard {
  person: AccessCardPerson | null
  warnings: AccessWarning[]
  sees: AccessCardSees[]
  inherits: AccessCardInherits | null
}

export interface AccessDecision {
  outcome: string
  /** Whether the person was rebuilt at once; if not, the next hourly update does it. */
  built: boolean | null
}

/** One Mindzer role on Access > HR roles. Counts only, no names. */
export interface AccessHrRoleRow {
  roleId: number
  roleName: string | null
  roleFunction: string | null
  inSource: boolean
  setUp: boolean
  /** Null when not set up. */
  hierarchyMode: number | null
  mobileAllowed: boolean | null
  reports: number
  columnGroups: number
  people: number
  approved: number
  canEnter: number
  version: string | null
  changedByEmail: string | null
}

export interface AccessHrRoles {
  roles: AccessHrRoleRow[]
}

/** One role, for the editor. */
export interface AccessHrRolePage {
  role: {
    roleId: number
    roleName: string | null
    roleFunction: string | null
    inSource: boolean
    setUp: boolean
    hierarchyMode: number | null
    mobileAllowed: boolean | null
    /** The version a save quotes; null when not set up. */
    version: string | null
    changedByEmail: string | null
    changeReason: string | null
    people: number
  } | null
  reports: { slug: string; displayName: string; allowed: boolean }[]
  /** Every column group of every report in use; the editor offers a report's under it once ticked (D53). */
  columnGroups: AccessHrRoleColumnGroup[]
}

/** One column group in the HR role editor (D53). */
export interface AccessHrRoleColumnGroup {
  groupId: number
  groupName: string
  description: string | null
  reportSlug: string
  reportName: string
  allowed: boolean
  /** The columns it shows, in the report's order ("Cost, GP, GP%"); null when it gates none (review R-22). */
  unlocks: string | null
  /** What the group means as read (report, name, description, columns); a save sends it back for each group it gives (51637). */
  groupKey: string
}

/** One holder and one report in the hierarchy preview (D49): rows of the last 12 months. */
export interface AccessHierarchyPreviewRow {
  mindzerUserId: number
  displayName: string | null
  compId: string | null
  staffDirect: number
  staffAll: number
  reportSlug: string
  reportName: string
  /** The report has a salesperson, so hierarchy cuts it; otherwise all three counts are the same. */
  hierarchyApplies: boolean
  rowsNow: number
  rowsType1: number
  rowsType2: number
  /** Their hierarchy today: 0 off, 1 or 2. */
  modeNow: number
  /** A person setting fixes their hierarchy, so the role's does not change it. */
  ownSetting: boolean
}

/** What hierarchy would change for a role's holders, counts only (D49). */
export interface AccessHierarchyPreview {
  preview: AccessHierarchyPreviewRow[]
  /** How many hold the role in all; the preview shows those with the most staff. */
  holders: number | null
  /** When the lines' salespeople were counted; null before the first count. */
  countedUtc: string | null
}

/** The whole state an HR role save sends (every part, [] for none). */
export interface AccessHrRoleBody {
  expectedVersion: string | null
  reports: string[]
  /** Column group IDs, each of a report the role gives (51627). */
  columnGroups: number[]
  hierarchy: 'off' | 'type1' | 'type2'
  phone: boolean
  reason?: string
  /** For a preview: of stopping the role's use. */
  remove?: boolean
  /** Each given group's key as this screen read it, by ID: a group changed since is refused (51637). */
  columnGroupKeys: Record<string, string>
}

/**
 * What a role change does (sec.usp_Admin_Set_Hr_Role_MBI's summary). Its keys are the
 * procedure's own spelling: the API passes the JSON through untouched.
 */
export interface AccessHrRoleSummary {
  Outcome: 'PREVIEW' | 'SAVED' | 'REMOVED'
  Version: string | null
  Holders: number
  Holders_Approved: number
  Can_Enter_Before: number
  Can_Enter_After: number
  People_Changed: number
  Entry_Gained: number
  Entry_Lost: number
  People_Widened: number
  People_Narrowed: number
  /** Report openings gained (or lost), summed over people. */
  Reports_Gained: number
  Reports_Lost: number
  Companies_Gained: number
  /** People who gain a column group: information only, never a freeze (D53). */
  Column_Groups_Gained: number
  /** Changed people who do not hold the role: managers above them, under type 2. */
  Managers_Affected: number
  Hierarchy_Changed: number
  Phone_Changed: number
  Kept_By_Personal_Setting: number
  Widens: boolean | number
}

export interface AccessHrRoleEffect {
  outcome: string
  summary: AccessHrRoleSummary | null
}

/** One category on Access > Problems, with how many items it holds. */
export interface AccessProblemCategory {
  category: string
  items: number
  /** What the count counts: people, roles, codes, links, updates, sign-ins, lines. */
  countsWhat: string
}

export interface AccessProblems {
  categories: AccessProblemCategory[]
}

/** One item of a category, all in one shape. */
export interface AccessProblemItem {
  category: string
  /** The person, when the item is about one. */
  mindzerUserId: number | null
  name: string | null
  compId: string | null
  /** What it is about: a badge, a role ID, a D365 code, a warning, an update. */
  code: string | null
  detail: string | null
  countOf: number | null
  atUtc: string | null
}

export interface AccessProblemItems {
  items: AccessProblemItem[]
  /** The category's items before the page. */
  total: number | null
}

/** Is MBI's copy of Mindzer up to date (sec.usp_Admin_Get_Sync_Status_MBI, STATUS). */
export interface AccessSyncStatus {
  state: 'NEVER' | 'HELD' | 'STALE' | 'FAILING' | 'UP_TO_DATE'
  nowUtc: string
  staleAfterMinutes: number
  lastAttemptId: number | null
  lastAttemptUtc: string | null
  lastAttemptOutcome: string | null
  lastAttemptCode: string | null
  lastGoodId: number | null
  lastGoodUtc: string | null
  minutesSinceGood: number | null
  people: number | null
  activePeople: number | null
  peopleBefore: number | null
  activePeopleBefore: number | null
  lastAppliedUtc: string | null
  lastAppliedRows: number | null
  heldPullId: number | null
  heldUtc: string | null
  heldCode: string | null
  heldDetail: string | null
  heldCanAccept: boolean
  acceptanceId: number | null
  acceptanceExpiresUtc: string | null
  codeListCountedUtc: string | null
  /** A day, yyyy-MM-dd. */
  weeklyFiguresThrough: string | null
  weeklyFiguresBuiltUtc: string | null
  /** Each check's limit, from sec.Sync_Setting_MBI, as JSON (17i; review R-24). Absent from an API before it. */
  limitsJson?: string | null
}

/** One hourly update attempt (counts and codes only: never a name or an email). */
export interface AccessSyncPull {
  pullId: number
  startedUtc: string
  finishedUtc: string | null
  durationMs: number | null
  outcome: string
  code: string | null
  detail: string | null
  fetchStatus: number | null
  fetchError: string | null
  people: number | null
  activePeople: number | null
  rowsChanged: number | null
  accessChanged: number | null
}

export interface AccessSync {
  status: AccessSyncStatus | null
  pulls: AccessSyncPull[]
}

/** A held or refused update: what it would change. */
export interface AccessHeldPull {
  pull: {
    pullId: number
    startedUtc: string
    outcome: string
    code: string | null
    detail: string | null
    people: number | null
    activePeople: number | null
    changes: number
    deliveryKept: boolean
    overtaken: boolean
    canAccept: boolean
    acceptanceId: number | null
    acceptedByEmail: string | null
    acceptanceExpiresUtc: string | null
  } | null
  changes: { changeKind: string; mindzerId: number | null; name: string | null; detail: string | null }[]
}

export interface AccessAcceptResult {
  /** ACCEPTED, or ALREADY when one was waiting. */
  outcome: string
  acceptanceId: number | null
  expiresUtc: string | null
}

/** A sign-in email domain: whose people may sign in. */
export interface AccessEmailDomain {
  emailDomain: string
  activePeople: number
  approved: number
  signIns: number
  changedByEmail: string | null
  changeReason: string | null
  changedUtc: string | null
}

export interface AccessEmailDomains {
  domains: AccessEmailDomain[]
}

export interface AccessEmailDomainEffect {
  outcome: string
  /** The procedure's own counts, keys as it spells them. */
  summary: Record<string, unknown> | null
}

/** One person reporting to someone (sec.usp_Admin_List_Staff_MBI). */
export interface AccessStaffRow {
  staffUserId: number
  displayName: string | null
  compId: string | null
  /** L line, F functional, LF both. */
  edge: 'L' | 'F' | 'LF'
  status: AccessPersonStatus
  hasStaff: boolean
}

export interface AccessStaff {
  staff: AccessStaffRow[]
  directTotal: number | null
  allTotal: number | null
}

/** One line of a person's history, newest first. */
export interface AccessHistoryRow {
  occurredUtc: string
  /** APPROVAL, SETTINGS, ROLE, MINDZER, MANAGERS or SIGN_IN. */
  kind: string
  what: string | null
  detail: string | null
  objectKey: string | null
  beforeJson: string | null
  afterJson: string | null
  actorEmail: string | null
  reason: string | null
}

export interface AccessHistory {
  history: AccessHistoryRow[]
}

/** A D365 code an admin may pick: a company (no company asked), or its units and teams. */
export interface AccessOrgCode {
  kind: 'COMPANY' | 'UNIT' | 'TEAM'
  compId: string
  unitCode: string | null
  teamCode: string | null
  rowsRecent: number | null
  mindzerName: string | null
}

export interface AccessOrgCodes {
  codes: AccessOrgCode[]
}

/** A person's own settings, the whole state (05 section 6.7). */
export interface AccessPersonSettingsBody {
  expectedVersion: string | null
  /** Slug to "role", "on" or "off". */
  reports: Record<string, 'role' | 'on' | 'off'>
  /** Column group ID to "role", "on" or "off" (D53); a new or changed one only of a main-scope report (51627). */
  columnGroups: Record<string, 'role' | 'on' | 'off'>
  hierarchy: 'role' | 'off' | 'type1' | 'type2'
  phone: 'role' | 'on' | 'off'
  /** Every main-scope line they keep, plus any new one. */
  lines: { effect: 'add' | 'remove'; unit: string | null; team: string | null; resume: boolean; company: string | null }[]
  reason?: string
  /** Each group's key as this page read it, by ID: a group switched on that changed since is refused (51637). */
  columnGroupKeys: Record<string, string>
}

/** An extra scope's whole state. */
export interface AccessExtraScopeBody {
  expectedVersion: string | null
  company: string | null
  unit: string | null
  team: string | null
  reports: string[]
  /** Column group IDs, each of a report it gives (51627). */
  columnGroups: number[]
  resume: boolean
  reason?: string
  /** For a preview: the scope changed (null for a new one) and whether it is being removed. */
  scopeId?: number | null
  remove?: boolean
  /** The scope's units and teams (owner, 29 Oct 2026): a unit with no team is the whole unit, its teams are
   *  those teams, a team with no unit is that team, neither is every unit. */
  lines?: { unit: string | null; team: string | null }[]
  /** Each group's key as this page read it, by ID: a group the line newly gives that changed since is refused (51637). */
  columnGroupKeys: Record<string, string>
}

/** What a change to one person does (Set_Person_Settings / Save_Extra_Scope summaries; their own keys). */
export interface AccessPersonEffectSummary {
  Outcome: string
  Version: string | null
  Can_Enter_Before: number | boolean | null
  Can_Enter_After: number | boolean | null
  Edits?: number | null
  Lines_Resumed?: number | null
  Hierarchy_Before?: number | null
  Hierarchy_After?: number | null
  Phone_Before?: boolean | number | null
  Phone_After?: boolean | number | null
  Extra_Scope_Id?: number | null
  Paused?: boolean | number | null
  People_Changed: number
  Entry_Gained: number
  Entry_Lost: number
  People_Widened: number
  People_Narrowed: number
  Reports_Gained: number
  Reports_Lost: number
  Companies_Gained: number
  Column_Groups_Gained: number
  Managers_Affected: number
  Widens: boolean | number
}

export interface AccessPersonEffect {
  outcome: string
  savedId?: number | null
  summary: AccessPersonEffectSummary | null
}

export interface AccessSignInLinkEffect {
  outcome: string
  summary: { Outcome: string; Approved: boolean | number | null; Can_Enter_After: boolean | number | null } | null
}
